import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { booksAPI, searchAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { decrypt, encrypt } from '../utils/security';
import { decryptAsync } from '../utils/decryptAsync';
import { useContentProtection } from '../utils/contentProtection';

const HL_COLORS_MAP = [
  { name: 'yellow', bg: '#FBF3D0', label: 'Yellow' },
  { name: 'red', bg: '#FAE8E6', label: 'Red' },
  { name: 'green', bg: '#E4F4EC', label: 'Green' },
  { name: 'blue', bg: '#EEF2F8', label: 'Blue' },
  { name: 'purple', bg: '#F0EDF8', label: 'Purple' },
];
// FIX: some chapter content in the DB contains an <iframe> whose src points
// back at this very app (e.g. ebook.aironline.in / the reader's own domain).
// Rendering that iframe loads a full second copy of the whole app inside the
// chapter — and if that chapter is reached again inside the iframe, it loads
// a THIRD copy inside that, and so on. This recursive self-embedding is what
// was spiking CPU/RAM and hanging the tab. We strip any iframe whose src
// resolves to our own origin (or a known list of our own domains) before it
// ever reaches the DOM.
const OWN_DOMAINS = [
  'ebook.aironline.in',
  'aironline.in',
  window.location.hostname,
];
const stripSelfEmbedIframes = (rawHtml) => {
  if (!rawHtml) return rawHtml;
  try {
    const tmp = document.createElement('div');
    tmp.innerHTML = rawHtml;

    const flagSelfEmbed = (el, srcAttr) => {
      try {
        const src = el.getAttribute(srcAttr) || '';
        if (!src) return false;
        const url = new URL(src, window.location.origin);
        return OWN_DOMAINS.some(d => d && url.hostname === d);
      } catch { return false; }
    };

    const replaceWithWarning = (el) => {
      const warn = document.createElement('div');
      warn.style.cssText = 'padding:12px 16px;margin:12px 0;background:#FAE8E6;border-left:3px solid #C97B77;border-radius:6px;font-size:12px;color:#8b2020;font-family:Nunito,sans-serif;';
      warn.textContent = '⚠ Embedded content removed (pointed back at this app).';
      el.replaceWith(warn);
    };

    // FIX (broadened): cover <iframe>, <frame>, <object data="...">, and
    // <embed src="..."> — not just <iframe src="...">. We can't inspect the
    // raw stored HTML directly, so this is defense-in-depth in case the
    // self-embed wasn't specifically an <iframe src="">.
    tmp.querySelectorAll('iframe, frame').forEach(f => {
      if (flagSelfEmbed(f, 'src')) replaceWithWarning(f);
    });
    tmp.querySelectorAll('object[data]').forEach(o => {
      if (flagSelfEmbed(o, 'data')) replaceWithWarning(o);
    });
    tmp.querySelectorAll('embed[src]').forEach(e => {
      if (flagSelfEmbed(e, 'src')) replaceWithWarning(e);
    });

    return tmp.innerHTML;
  } catch {
    return rawHtml;
  }
};

const applyHighlightsToHtml = (rawHtml, highlights = []) => {
  if (!rawHtml) return rawHtml;
  let h = stripSelfEmbedIframes(rawHtml);

  // Fix split URLs — URL broken across lines
  h = h.replace(/(https?:\/\/\S+)\s+([\w\/._~%\-]+\.(?:pdf|doc|html|htm|php|asp|aspx|jpg|png))/gi, '$1$2');

  // Make URLs clickable
  // FIX: previously only skipped URLs already inside href="..." — but URLs
  // living inside src="..." (e.g. a legitimate <iframe src="https://...">)
  // were NOT skipped, so this regex reached into the iframe's src attribute
  // and wrapped the URL text with an <a href> tag, corrupting the iframe
  // markup. The browser then fell back to treating the mangled src as a
  // relative path against our own origin — loading the whole app inside
  // the broken iframe. That's the actual root cause of the self-embedding
  // bug; it was never bad stored content, it was this regex breaking
  // legitimate video embeds. Now skips any URL inside any quoted attribute
  // value (src=, href=, data=, action=, etc.), not just href=.
  h = h.replace(
    /(?<![a-zA-Z-]+=["'][^"']{0,500})(https?:\/\/[^\s<>"'\]]+)/g,
    (match) => {
      const clean = match.replace(/[.,;:)\]'"]+$/, '');
      return `<a href="${clean}" target="_blank" rel="noopener" style="color:#1a6fc4;text-decoration:underline;word-break:break-all;">${clean}</a>`;
    }
  );

  // AIR Citations clickable — e.g. AIR 1951 Mad 107, AIR 1978 SC 282
  h = h.replace(
    /(<a\b[^>]*>[\s\S]*?<\/a>)|(<[^>]+>)|(AIR\s+(\d{4})\s+([A-Z][a-zA-Z]*(?:\s+[A-Z][a-zA-Z]*)?)\s+(\d+))/gi,
    (match, aTag, tag, full, year, court, page) => {
      if (aTag) return aTag;
      if (tag) return tag;
      const citation = encodeURIComponent(`AIR ${year} ${court} ${page}`);
      return `<a href="https://www.aironline.in/specific-search/citation-search/?citation=${citation}" target="_blank" rel="noopener" style="color:#C97B77;text-decoration:underline;font-weight:600;cursor:pointer;">${full}</a>`;
    }
  );

  highlights.forEach(hl => {
    if (!hl.text || hl.text.length < 2) return;
    const hlBg = HL_COLORS_MAP.find(c => c.name === hl.color)?.bg || '#FBF3D0';
    const safe = hl.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      h = h.replace(
        new RegExp(`(?![^<>]*>)(${safe})`, 'g'),
        `<mark style="background:${hlBg};border-radius:2px;padding:0 2px;">$1</mark>`
      );
    } catch { }
  });
  return h;
};

const cleanText = (text) => {
  if (!text) return '';
  let cleaned = text.trim();
  // Strip list item prefixes: "1. ", "(a) ", "a. ", "I. ", etc.
  cleaned = cleaned.replace(/^(\d+|[a-zA-Z]|\b[IVXLCDM]+\b)[\.\)-]+[\s-]*/, '');
  cleaned = cleaned.replace(/^\((?:\d+|[a-zA-Z]|\b[IVXLCDM]+\b)\)[\.\s-]*/, '');
  return cleaned
    .replace(/[\u201C\u201D\u2018\u2019]/g, '"')
    .replace(/&ldquo;|&rdquo;|&lsquo;|&rsquo;/gi, '"')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .toLowerCase()
    .trim();
};

const normalizeSecKey = (rawStr) => {
  if (!rawStr) return '';
  const str = String(rawStr).trim();
  if (/preamble/i.test(str)) return 'preamble';
  const m = str.match(/(?:\b(?:s|sec|section)\b|\bS)\.?\s*(\d+[A-Z]?)/i);
  if (m) return `section ${m[1].toLowerCase()}`;
  return cleanText(str);
};

const isFootnoteParagraph = (el) => {
  if (!el) return false;

  // Headings (h1-h6, .articleH2) are NEVER footnotes
  if (el.matches ? el.matches('h1, h2, h3, h4, h5, h6, .articleH2') : false) return false;

  const text = el.textContent ? el.textContent.trim() : '';
  if (!text) return false;

  // Check if this element is a pure bold title heading like <p><b>5. Laws relating...</b></p>
  const children = Array.from(el.children || []);
  const isPureBold = children.length === 1 && (children[0].tagName === 'B' || children[0].tagName === 'STRONG') && children[0].textContent.trim() === text;

  // Check for legal citation markers or footnote keywords
  const hasCitationMarker = /\b(AIR|SC|PC|Cal|Mad|Bom|Raj|Rang|ILR|AIROnline|SCC|Supp|See|v\.|vs\.|p\.|pp\.)\b/i.test(text) || /\(\d{4}\)/.test(text);

  // Pure bold heading without citation markers is a section heading, NOT a footnote
  if (isPureBold && !hasCitationMarker) return false;

  // 1. font-size on the element itself
  const pStyle = el.getAttribute('style') || '';
  const ownFsMatch = pStyle.match(/font-size:\s*([\d.]+)pt/);
  if (ownFsMatch && parseFloat(ownFsMatch[1]) <= 9.5) return true;

  // 2. font-size on the first child span (PDF Studio puts it here)
  const firstSpan = el.querySelector('span[style]');
  if (firstSpan) {
    const spanStyle = firstSpan.getAttribute('style') || '';
    const spanFsMatch = spanStyle.match(/font-size:\s*([\d.]+)pt/);
    if (spanFsMatch && parseFloat(spanFsMatch[1]) <= 9.5) return true;
  }

  // 3. line-height on the <p> as a signal (footnote rows have small line-height)
  const lhMatch = pStyle.match(/line-height:\s*([\d.]+)pt/);
  if (lhMatch && parseFloat(lhMatch[1]) <= 9.5) return true;

  // 4. Accept <li> items inside any list
  if (el.tagName === 'LI') return true;

  // 5. Accept any paragraph containing legal citation markers
  if (hasCitationMarker) return true;

  // 6. Numbered footnote pattern (e.g. "5. ...") on non-heading paragraphs
  if (/^\[?\d+\]?[\.\s\u2013\u2014-]+/.test(text) && !isPureBold) return true;

  return false;
};

const parseFootnotes = (scanDiv) => {
  const fnMap = {};
  const secNoteMap = {};
  if (!scanDiv) return { fnMap, secNoteMap };

  const hSel = 'h1, h2, h3, h4';
  let curSecId = 'global';
  let curNote = '0';
  let curNoteTitle = '';

  const nodes = scanDiv.querySelectorAll(
    hSel + ', ol.fn-list, ol.clist, p.fn, p, li, span.fn-tt-line, div.fn, div.footnote, p.footnote'
  );

  nodes.forEach(node => {
    if (node.matches(hSel)) {
      const headingText = node.textContent.trim();
      const noteHeadingMatch = headingText.match(/^(.*?)\s*[\u2014\u2013\-\:\u2010-\u2015]?\s*Note\s*(\d+[A-Z]?)/i);
      if (noteHeadingMatch) {
        curSecId = normalizeSecKey(noteHeadingMatch[1]);
        curNote = noteHeadingMatch[2].toUpperCase();
        curNoteTitle = headingText;
      } else {
        if (node.id) curSecId = normalizeSecKey(node.id);
        else curSecId = normalizeSecKey(headingText) || curSecId;
        const m = headingText.match(/note\s*(\d+[A-Z]?)/i);
        if (m) curNote = m[1].toUpperCase();
        else if (node.id) curNote = '0';
      }
    } else {
      const text = node.textContent.trim();
      if (!text) return;

      const processItem = (num, txt) => {
        if (!txt) return;
        const formattedTxt = /^\d+[\.\s-]/.test(txt) ? txt : `${num}. ${txt}`;
        fnMap[num] = formattedTxt;

        if (!secNoteMap[curSecId]) secNoteMap[curSecId] = {};
        if (!secNoteMap[curSecId][curNote]) {
          secNoteMap[curSecId][curNote] = {
            title: curNoteTitle || `Note ${curNote}`,
            items: {},
            itemLines: []
          };
        }
        secNoteMap[curSecId][curNote].items[num] = formattedTxt;
        secNoteMap[curSecId][curNote].itemLines.push(formattedTxt);
      };

      if (node.tagName === 'LI') {
        let num = null;
        let txt = text;
        const cm = text.match(/^\[?(\d+)\]?[\.\s-]*([\s\S]+)/);
        if (cm) {
          num = parseInt(cm[1], 10);
          txt = cm[2].trim();
        } else {
          const valAttr = node.getAttribute('value');
          if (valAttr && /^\d+$/.test(valAttr)) {
            num = parseInt(valAttr, 10);
          } else {
            const parent = node.parentElement;
            if (parent && (parent.tagName === 'OL' || parent.classList.contains('fn-list') || parent.classList.contains('clist'))) {
              let count = 1;
              let sib = node.previousElementSibling;
              while (sib) {
                if (sib.tagName === 'LI') count++;
                sib = sib.previousElementSibling;
              }
              const startAttr = parent.getAttribute('start');
              if (startAttr && /^\d+$/.test(startAttr)) {
                count += (parseInt(startAttr, 10) - 1);
              }
              num = count;
            }
          }
        }
        if (num !== null && txt) {
          processItem(num, txt);
        }
      } else {
        if (!isFootnoteParagraph(node)) return;
        const parts = text.split(/(?=\b\d+\.\s+[A-Z])|(?=\b\[\d+\]\s+[A-Z])/);
        parts.forEach(part => {
          const cm = part.trim().match(/^\[?(\d+)\]?[\.\s-]+([\s\S]*)/);
          if (cm) {
            const num = parseInt(cm[1], 10);
            const txt = cm[2].trim();
            if (txt) processItem(num, txt);
          }
        });
      }
    }
  });

  return { fnMap, secNoteMap };
};

const tagSuperscripts = (containerEl, secNoteMap, fnMap, chapterTitle) => {
  if (!containerEl) return;

  // Pre-scan for lower-alpha <ol>/<ul>/<li text> lists in section content
  const alphaSecMap = {};
  let tempSecKey = normalizeSecKey(chapterTitle) || 'global';

  const allScanNodes = containerEl.querySelectorAll('h1, h2, h3, h4, ol, ul, li');
  allScanNodes.forEach(node => {
    if (node.matches('h1, h2, h3, h4')) {
      const norm = normalizeSecKey(node.textContent.trim());
      if (norm && (norm.startsWith('section') || norm === 'preamble')) {
        tempSecKey = norm;
      }
    } else if (node.matches('ol, ul')) {
      const styleAttr = node.getAttribute('style') || '';
      const typeAttr = node.getAttribute('type') || '';
      const isAlpha = styleAttr.includes('lower-alpha') || typeAttr === 'a';
      if (!isAlpha) return;

      const startAttr = node.getAttribute('start');
      const startIdx = (startAttr && /^\d+$/.test(startAttr)) ? (parseInt(startAttr, 10) - 1) : 0;

      if (!alphaSecMap[tempSecKey]) alphaSecMap[tempSecKey] = {};
      const lis = Array.from(node.children).filter(c => c.tagName === 'LI');
      lis.forEach((li, idx) => {
        const text = li.textContent.trim();
        if (!text) return;

        const prefixMatch = text.match(/^\[?\s*([a-zA-Z])\s*[\.\)\]]/);
        const letter = prefixMatch ? prefixMatch[1].toLowerCase() : String.fromCharCode(97 + startIdx + idx);

        const cleanTxt = text.replace(/^\[?\s*[a-zA-Z]\s*[\.\)\]]\s*/, '').trim();
        alphaSecMap[tempSecKey][letter] = cleanTxt || text;
      });
    } else if (node.matches('li')) {
      const text = node.textContent.trim();
      const prefixMatch = text.match(/^\[?\s*([a-zA-Z])\s*[\.\)\]]/);
      if (prefixMatch) {
        const letter = prefixMatch[1].toLowerCase();
        if (!alphaSecMap[tempSecKey]) alphaSecMap[tempSecKey] = {};
        const cleanTxt = text.replace(/^\[?\s*[a-zA-Z]\s*[\.\)\]]\s*/, '').trim();
        alphaSecMap[tempSecKey][letter] = cleanTxt || text;
      }
    }
  });

  const nodes = containerEl.querySelectorAll('h1, h2, h3, h4, sup, .superScript, .fn-ref');
  let curSecKey = normalizeSecKey(chapterTitle) || 'global';

  nodes.forEach(node => {
    if (node.matches('h1, h2, h3, h4')) {
      const text = node.textContent.trim();
      const norm = normalizeSecKey(text);
      if (norm && (norm.startsWith('section') || norm === 'preamble')) {
        curSecKey = norm;
      }
    }

    if (node.matches('sup, .superScript, .fn-ref')) {
      const trimmedText = node.textContent.trim();

      // Check if superscript is an alphabet letter (e.g. "a", "b", "c", "[a]", "[b]")
      const alphaMatch = trimmedText.match(/\[?([a-zA-Z])\]?/);
      const isAlphaSup = alphaMatch && !/\d/.test(trimmedText);

      if (isAlphaSup) {
        const letter = alphaMatch[1].toLowerCase();
        node.classList.add('fn-ref');
        node.dataset.sec = curSecKey;
        node.dataset.cite = letter;

        let alphaText = '';
        if (alphaSecMap[curSecKey] && alphaSecMap[curSecKey][letter]) {
          alphaText = alphaSecMap[curSecKey][letter];
        }

        if (alphaText) {
          node.dataset.note = `Point (${letter})`;
          node.dataset.text = alphaText;
        } else {
          node.dataset.note = `Point (${letter})`;
        }
        return;
      }

      // Existing numeric footnote logic
      const numMatch = trimmedText.match(/\d+[A-Z]?/i);
      if (!numMatch) return;

      const citeNumStr = numMatch[0].toUpperCase();
      const citeNumInt = parseInt(citeNumStr, 10);
      node.classList.add('fn-ref');
      node.dataset.sec = curSecKey;
      node.dataset.cite = citeNumStr;

      let resolvedText = '';
      let matchedNoteObj = null;

      // 1. Match by curSecKey + citeNumStr as Note Number (e.g. Preamble + Note 1, Section 2 + Note 8)
      if (curSecKey && secNoteMap[curSecKey] && secNoteMap[curSecKey][citeNumStr]) {
        matchedNoteObj = secNoteMap[curSecKey][citeNumStr];
      }

      // 2. Try fuzzy section key match in secNoteMap
      if (!matchedNoteObj && curSecKey) {
        const secKeys = Object.keys(secNoteMap);
        const matchedKey = secKeys.find(k => k === curSecKey || k.includes(curSecKey) || curSecKey.includes(k));
        if (matchedKey && secNoteMap[matchedKey] && secNoteMap[matchedKey][citeNumStr]) {
          matchedNoteObj = secNoteMap[matchedKey][citeNumStr];
        }
      }

      if (matchedNoteObj) {
        resolvedText = matchedNoteObj.itemLines ? matchedNoteObj.itemLines.join('\n') : '';
        node.dataset.note = matchedNoteObj.title || `Note ${citeNumStr}`;
      } else if (fnMap[citeNumInt]) {
        resolvedText = fnMap[citeNumInt];
        node.dataset.note = `Note ${citeNumStr}`;
      }

      if (resolvedText) {
        node.dataset.text = resolvedText;
      }
    }
  });

  return { alphaSecMap };
};

// ── PER-PAGE FOOTNOTE TAGGING ─────────────────────────────────────────────────
// PDF-to-HTML chapters contain many <div id="pageXXX"> blocks. Each page resets
// its footnote numbering (1, 2, 3, ...) independently. The global fnMap built by
// parseFootnotes/tagSuperscripts always has the LAST page's footnote 3 stored for
// key 3, so every earlier page's fn-ref (3) shows the wrong citation.
//
// This function re-scans each page div individually, builds a page-local fnMap,
// and stamps data-text on every sup.fn-ref within that page div so the tooltip
// always shows the correct citation for that specific page.
const tagPerPageFootnotes = (containerEl) => {
  if (!containerEl) return;

  // PERF FIX: scope strictly to this chunk only — never scan the whole Shadow DOM.
  // Previously getRootNode() walked up to the ShadowRoot and scanned ALL 500 pages
  // on every scroll frame, causing 50,000-node traversals that froze the main thread.
  let pageDivs = Array.from(containerEl.querySelectorAll('div[id^="page"]'))
    .filter(el => /^page\d+$/.test(el.id));

  // Edge case: if containerEl itself is the page div or containerEl has no nested page divs
  if (pageDivs.length === 0) {
    const parentPage = containerEl.closest ? containerEl.closest('div[id^="page"]') : null;
    if (parentPage && /^page\d+$/.test(parentPage.id)) {
      pageDivs = [parentPage];
    } else if (containerEl.id && /^page\d+$/.test(containerEl.id)) {
      pageDivs = [containerEl];
    }
  }

  if (pageDivs.length === 0) return; // Not a multi-page chunk — skip.

  pageDivs.forEach(pageDiv => {
    const pageId = pageDiv.id;

    // ── 1. Build local fnMap for this page ────────────────────────────────────
    const localFnMap = {};

    const paragraphs = Array.from(pageDiv.querySelectorAll('p, li'));
    paragraphs.forEach(p => {
      const text = p.textContent.trim();
      if (!text) return;

      // Match numbered citation: "3. AIR..." or "3 AIR..." or "[3] AIR..."
      const cm = text.match(/^\[?(\d+)\]?[.\s\u2013\u2014-]+(.+)/);
      if (!cm) return;

      const num = parseInt(cm[1], 10);
      if (!num || num > 999) return; // Sanity check

      // Must be a footnote paragraph (small font or list item)
      if (!isFootnoteParagraph(p)) return;

      const txt = cm[2].trim();
      if (txt.length < 3) return; // Skip garbage

      const formattedTxt = `${num}. ${txt}`;
      // Only store the FIRST occurrence of each number per page (that's the actual
      // footnote for this page — later overrides come from the next page's content).
      if (!localFnMap[num]) {
        localFnMap[num] = formattedTxt;
      }
    });

    if (Object.keys(localFnMap).length === 0) return; // No footnotes on this page.

    // ── 2. Tag all sup.fn-ref elements within this page div ───────────────────
    const sups = Array.from(pageDiv.querySelectorAll('sup.fn-ref, sup.superScript, .fn-ref'));
    sups.forEach(sup => {
      const rawText = sup.textContent.trim();
      // Only handle numeric superscripts
      const numMatch = rawText.match(/\d+/);
      if (!numMatch) return;

      const num = parseInt(numMatch[0], 10);
      if (!num || !localFnMap[num]) return;

      // Stamp page-local footnote text — overrides the global (wrong) value.
      sup.dataset.text = localFnMap[num];
      sup.dataset.note = `Ref ${num}`;
      sup.dataset.pageId = pageId;
    });
  });
};

const resolveSupData = (superEl, secNoteMap, alphaSecMap, fnMap) => {
  if (!superEl) return { fnText: '', fnNote: '', fnCite: '' };
  const rawText = superEl.textContent.trim();
  const secKey = superEl.dataset?.sec || 'global';

  const alphaMatch = rawText.match(/\[?([a-zA-Z])\]?/);
  const isAlpha = alphaMatch && !/\d/.test(rawText);

  if (isAlpha) {
    const letter = alphaMatch[1].toLowerCase();
    let fnText = '';
    if (alphaSecMap && alphaSecMap[secKey] && alphaSecMap[secKey][letter]) {
      fnText = alphaSecMap[secKey][letter];
    }
    if (!fnText && alphaSecMap) {
      const matchKey = Object.keys(alphaSecMap).find(k => k === secKey || k.includes(secKey) || secKey.includes(k));
      if (matchKey && alphaSecMap[matchKey] && alphaSecMap[matchKey][letter]) {
        fnText = alphaSecMap[matchKey][letter];
      }
    }
    const fnNote = `Point (${letter})`;
    return { fnText, fnNote, fnCite: letter };
  }

  // Numeric superscripts
  let fnText = '';
  let fnNote = '';
  let fnCite = superEl.dataset?.cite || rawText;

  const numMatch = rawText.match(/\d+[A-Z]?/i);
  if (numMatch) {
    const citeNumStr = numMatch[0].toUpperCase();
    const citeNumInt = parseInt(citeNumStr, 10);
    fnCite = citeNumStr;

    // ── 1. PAGE-LOCAL SCAN FIRST ──
    // Search the page div for this specific page's footnote matching the number
    const pageDiv = superEl.closest('div[id^="page"]');
    if (pageDiv) {
      const paragraphs = Array.from(pageDiv.querySelectorAll('p, li'));
      for (const p of paragraphs) {
        if (!isFootnoteParagraph(p)) continue;
        const text = p.textContent.trim();
        if (text) {
          const cm = text.match(/^\[?(\d+)\]?[.\s\u2013\u2014-]+(.+)/);
          if (cm && parseInt(cm[1], 10) === citeNumInt) {
            fnText = `${citeNumInt}. ${cm[2].trim()}`;
            fnNote = `Ref ${citeNumStr}`;
            break;
          }
        }
      }
    }

    // ── 2. FALLBACK TO STAMPED DATASET / SEC-NOTE-MAP / FN-MAP ──
    if (!fnText) {
      fnText = superEl.dataset?.text || '';
      fnNote = superEl.dataset?.note || '';
    }

    if (!fnText && secNoteMap && secNoteMap[secKey] && secNoteMap[secKey][citeNumStr]) {
      fnText = secNoteMap[secKey][citeNumStr].itemLines ? secNoteMap[secKey][citeNumStr].itemLines.join('\n') : '';
      fnNote = secNoteMap[secKey][citeNumStr].title || `Note ${citeNumStr}`;
    } else if (!fnText && fnMap && fnMap[citeNumInt]) {
      fnText = fnMap[citeNumInt];
      fnNote = `Note ${citeNumStr}`;
    }
  }

  return { fnText, fnNote: fnNote || `Ref ${fnCite}`, fnCite };
};

const preprocessSynopsis = (rootEl) => {
  if (!rootEl) return;
  const headings = Array.from(rootEl.querySelectorAll('h1, h2, h3, h4, .articleH2'));
  const synopsisHeadings = headings.filter(h => h.textContent.trim().toLowerCase() === 'synopsis');
  if (synopsisHeadings.length === 0) return;

  let synIndex = 0;

  synopsisHeadings.forEach(synopsisHeading => {
    // Walk UP from the synopsis heading to find its direct child of rootEl.
    // This is necessary because the heading may be nested inside <ol><li><h2>Synopsis</h2></li></ol>
    let topLevel = synopsisHeading;
    while (topLevel.parentElement && topLevel.parentElement !== rootEl) {
      topLevel = topLevel.parentElement;
    }

    // The break selector: stop scanning when we hit an element that IS or CONTAINS an article heading
    const breakSelector = '.articleH2, h1, h2, h3, h4';

    // Collect sibling lists that come AFTER the synopsis top-level container
    const lists = [];
    let next = topLevel.nextElementSibling;
    while (next) {
      // Stop if next sibling is itself a heading or WRAPS a heading (e.g. <ol><li><h2>...</h2></li></ol>)
      if (next.matches(breakSelector) || next.querySelector(breakSelector)) break;
      if (next.tagName === 'OL' || next.tagName === 'UL') {
        lists.push(next);
      } else {
        next.querySelectorAll('ol, ul').forEach(l => lists.push(l));
      }
      next = next.nextElementSibling;
    }

    lists.forEach(listEl => {
      // PERF FIX: this used to recompute `headingsBelow` (a filter over EVERY
      // heading in the whole chapter via compareDocumentPosition) AND re-run the
      // full fuzzy cleanText/word-overlap comparison against each candidate --
      // for every single <li>. On a large statute chapter (hundreds of headings x
      // hundreds of synopsis items) that's O(headings x items) of expensive string
      // work running synchronously in one block. reader.html's applySynopsisLinking
      // already solved this with a precomputed Map for O(1) exact lookups; ported
      // that here, computed ONCE per list instead of once per <li>.
      const headingsBelow = headings.filter(h => {
        return !!(listEl.compareDocumentPosition(h) & Node.DOCUMENT_POSITION_FOLLOWING);
      });
      if (headingsBelow.length === 0) return;

      const headingMap = new Map();
      const headingEntries = headingsBelow.map(h => {
        const cleaned = cleanText(h.textContent);
        if (cleaned) headingMap.set(cleaned, h);
        return { cleaned, element: h };
      });

      Array.from(listEl.querySelectorAll('li')).forEach(li => {
        const rawText = li.textContent.trim();
        if (!rawText) return;
        const cleanedSyn = cleanText(rawText);
        if (!cleanedSyn) return;

        // Fast O(1) exact match first.
        let match = headingMap.get(cleanedSyn);

        // Fallback: same fuzzy substring/word-overlap logic as before, but only
        // runs for the (much rarer) non-exact-match case, and reuses the
        // already-cleaned heading text instead of recomputing it per <li>.
        if (!match) {
          const entry = headingEntries.find(({ cleaned: cleanedH }) => {
            if (!cleanedH) return false;
            const simpleH = cleanedH.replace(/\s+/g, '');
            const simpleS = cleanedSyn.replace(/\s+/g, '');
            if (simpleH === simpleS) return true;
            // Substring match only valid when lengths are within 60% ratio (prevents "india" matching "offencecommittedwithinindia")
            const lenRatio = Math.min(simpleH.length, simpleS.length) / Math.max(simpleH.length, simpleS.length);
            if (lenRatio >= 0.6 && (simpleH.includes(simpleS) || simpleS.includes(simpleH))) return true;

            // Fallback: Word-based match (80%+ overlap of meaningful words)
            const wordsH = cleanedH.split(/\s+/).filter(w => w.length > 2 && isNaN(w));
            const wordsS = cleanedSyn.split(/\s+/).filter(w => w.length > 2 && isNaN(w));
            if (wordsH.length === 0 || wordsS.length === 0) return false;
            const shorter = wordsH.length < wordsS.length ? wordsH : wordsS;
            const longer = wordsH.length < wordsS.length ? wordsS : wordsH;
            return (shorter.filter(w => longer.includes(w)).length / shorter.length) >= 0.8;
          });
          if (entry) match = entry.element;
        }

        if (match) {
          synIndex++;
          const synId = `syn_${synIndex}`;
          const targetId = `syn_target_${synIndex}`;
          li.setAttribute('data-synopsis-id', synId);
          li.style.cursor = 'pointer';
          li.style.textDecoration = 'underline';
          li.style.color = 'var(--rose, #C97B77)';

          match.setAttribute('id', targetId);
          match.setAttribute('data-synopsis-target-id', synId);

          if (!match.querySelector('.back-to-synopsis')) {
            const chevron = document.createElement('span');
            chevron.className = 'back-to-synopsis';
            chevron.setAttribute('data-back-to', synId);
            chevron.style.cssText = 'cursor:pointer; margin-left:8px; color:var(--rose, #C97B77); font-family:var(--font-u); font-weight:bold; font-size:0.85em; display:inline-block; vertical-align:middle;';
            chevron.innerHTML = '▲';
            chevron.title = 'Back to Synopsis';
            match.appendChild(chevron);
          }
        }
      });
    });
  });
};

// ── CONTENT PROTECTION: tiled watermark ──────────────────────────────────
// This can't PREVENT a screenshot or screen recording — no web technology
// can, since the browser has to hand the OS actual rendered pixels. What it
// CAN do is make any leaked screenshot traceable back to the account that
// produced it, which is the same "attribution over prevention" approach
// already used for the offline USB product's watermarking. It's applied as
// a CSS background-image (not a DOM element) on the shadow-root content div,
// specifically so there's no single node a casual "delete this div" attempt
// in DevTools would remove — and per the isWiped logic below, having
// DevTools open at all blanks the real content anyway, so there's nothing
// sensitive visible to edit around it in the first place.
const buildWatermarkBackground = (label) => {
  const safe = String(label || 'AIR Online').replace(/[<>&"']/g, '');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='460' height='200'>` +
    `<text x='10' y='60' font-family='Nunito,sans-serif' font-size='13' fill='rgba(120,110,95,0.14)' transform='rotate(-26 230 100)'>${safe}</text>` +
    `<text x='10' y='150' font-family='Nunito,sans-serif' font-size='13' fill='rgba(120,110,95,0.14)' transform='rotate(-26 230 100)'>${safe}</text>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const WIPE_MESSAGES = {
  inactive: 'Content hidden while this window is inactive.',
  devtools: 'Content hidden while developer tools are open.',
  print: 'Printing is disabled for this content.',
};

const ShadowContent = React.forwardRef(({ html, footnoteHtml, highlights = [], style, isWiped, wipeReason, watermarkText, onLinkClick, disableLazy, chapterTitle, chunkDownloaderRef }, ref) => {
  const containerRef = useRef(null);
  const shadowRootRef = useRef(null);
  const secureMemory = useRef(new Map());
  const chunksMetadata = useRef(new Map());
  // PERF: read via highlightsRef.current inside the heavy content-mount effect
  // instead of depending on `highlights` directly, so adding/removing a
  // highlight doesn't re-trigger a full decrypt + rebuild of the chapter.
  // See the dedicated incremental-highlight effect below, which restamps only
  // the already-rendered chunks when `highlights` changes.
  const highlightsRef = useRef(highlights);

  const scrollToElement = useCallback((targetEl) => {
    if (!targetEl) return;
    let scrollParent = containerRef.current?.parentElement;
    while (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
      if (scrollParent.scrollHeight > scrollParent.clientHeight) {
        const overflowY = window.getComputedStyle(scrollParent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') break;
      }
      scrollParent = scrollParent.parentElement;
    }

    const doPositionScroll = () => {
      if (scrollParent && scrollParent.scrollHeight > scrollParent.clientHeight) {
        const targetRect = targetEl.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const offset = targetRect.top - parentRect.top + scrollParent.scrollTop;
        scrollParent.scrollTop = Math.max(0, offset - 20);
      } else {
        targetEl.scrollIntoView({ block: 'start' });
      }
    };

    doPositionScroll();
    setTimeout(doPositionScroll, 100);
  }, []);

  React.useImperativeHandle(ref, () => ({
    highlightText: (term, occurrence) => {
      if (!shadowRootRef.current || !term) return;
      const contentDiv = shadowRootRef.current.querySelector('.content');
      if (!contentDiv) return;
      contentDiv.querySelectorAll('.search-temp-highlight').forEach(el => {
        const parent = el.parentNode;
        if (parent) { parent.replaceChild(document.createTextNode(el.textContent), el); parent.normalize(); }
      });
      let globalCount = 0;
      let targetChunkId = null;
      let localOccIndex = -1;
      for (const [id, meta] of chunksMetadata.current.entries()) {
        const text = meta.text.toLowerCase();
        let pos = text.indexOf(term.toLowerCase());
        while (pos !== -1) {
          if (globalCount === occurrence) {
            targetChunkId = id;
            let subPos = text.indexOf(term.toLowerCase());
            let subCount = 0;
            while (subPos < pos) { subCount++; subPos = text.indexOf(term.toLowerCase(), subPos + 1); }
            localOccIndex = subCount;
            break;
          }
          globalCount++;
          pos = text.indexOf(term.toLowerCase(), pos + 1);
        }
        if (targetChunkId) break;
      }
      if (targetChunkId) {
        const wrapper = contentDiv.querySelector(`[data-chunk-id="${targetChunkId}"]`);
        if (wrapper) {
          if (wrapper.getAttribute('data-decrypted') !== 'true') {
            const chunkHtml = secureMemory.current.get(targetChunkId);
            wrapper.innerHTML = applyHighlightsToHtml(chunkHtml, highlights);
            wrapper.setAttribute('data-decrypted', 'true');
          }
          let count = 0;
          const walk = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT, null, false);
          let node;
          while ((node = walk.nextNode())) {
            const txt = node.nodeValue.toLowerCase();
            const t = term.toLowerCase();
            let p = txt.indexOf(t);
            while (p !== -1) {
              if (count === localOccIndex) {
                const range = document.createRange();
                range.setStart(node, p);
                range.setEnd(node, p + term.length);
                const mark = document.createElement('mark');
                mark.className = 'search-temp-highlight';
                mark.style.background = '#ffd54f';
                mark.style.color = '#000';
                mark.style.padding = '0 2px';
                mark.style.borderRadius = '2px';
                mark.style.boxShadow = '0 0 0 2px #ffd54f';
                range.surroundContents(mark);
                mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => {
                  if (mark && mark.parentNode) {
                    const parent = mark.parentNode;
                    parent.replaceChild(document.createTextNode(mark.textContent), mark);
                    parent.normalize();
                  }
                }, 2500);
                return;
              }
              count++;
              p = txt.indexOf(t, p + 1);
            }
          }
          wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    get shadowRoot() { return shadowRootRef.current; },
    scrollIntoView: (args) => containerRef.current?.scrollIntoView(args),
    // scrollToAnchor — navigate to an anchor ID inside the shadow DOM.
    // Works even for lazy-unrendered chunks by scanning secureMemory directly.
    scrollToAnchor: (anchorId) => {
      if (!shadowRootRef.current || !anchorId) return false;
      const contentDiv = shadowRootRef.current.querySelector('.content');
      if (!contentDiv) return false;

      // Step 1: Direct hit — anchor in a rendered chunk
      const direct = contentDiv.querySelector(`#${CSS.escape(anchorId)}`) ||
        contentDiv.querySelector(`[id="${anchorId}"]`);
      if (direct) {
        const curNum = parseInt(anchorId.replace(/\D/g, ''), 10);
        if (!isNaN(curNum) && curNum <= 25) {
          const links = Array.from(direct.querySelectorAll('a[href^="#page"]'));
          const innerSecLink = links.find(a => {
            const h = a.getAttribute('href') || '';
            const num = parseInt(h.replace(/\D/g, ''), 10);
            return !isNaN(num) && num > 25;
          });
          if (innerSecLink) {
            const innerHref = innerSecLink.getAttribute('href') || '';
            const innerId = innerHref.startsWith('#') ? innerHref.substring(1) : '';
            if (innerId && onLinkClick) {
              onLinkClick({ type: 'anchor', value: innerId });
              return true;
            }
          }
        }
        scrollToElement(direct);
        return true;
      }

      const idPatterns = [`id="${anchorId}"`, `id='${anchorId}'`];
      let targetChunkId = null;
      for (const [chunkId, rawHtml] of secureMemory.current.entries()) {
        if (rawHtml && idPatterns.some(p => rawHtml.includes(p))) {
          targetChunkId = chunkId;
          break;
        }
      }

      if (!targetChunkId) {
        if (chunkDownloaderRef?.current) {
          chunkDownloaderRef.current(true);
        }
        return false;
      }

      // Force-render the target chunk, then scroll to the anchor
      const wrapper = contentDiv.querySelector(`[data-chunk-id="${targetChunkId}"]`);
      if (!wrapper) return false;

      if (wrapper.getAttribute('data-decrypted') !== 'true') {
        const chunkHtml = secureMemory.current.get(targetChunkId);
        wrapper.innerHTML = applyHighlightsToHtml(chunkHtml, highlights);
        wrapper.setAttribute('data-decrypted', 'true');
      }

      // Wait 1 frame for browser DOM layout reflow before measuring height and scrolling
      requestAnimationFrame(() => {
        const target = wrapper.querySelector(`#${CSS.escape(anchorId)}`) ||
          wrapper.querySelector(`[id="${anchorId}"]`);
        if (target) {
          const curNum = parseInt(anchorId.replace(/\D/g, ''), 10);
          if (!isNaN(curNum) && curNum <= 25) {
            const links = Array.from(target.querySelectorAll('a[href^="#page"]'));
            const innerSecLink = links.find(a => {
              const h = a.getAttribute('href') || '';
              const num = parseInt(h.replace(/\D/g, ''), 10);
              return !isNaN(num) && num > 25;
            });
            if (innerSecLink) {
              const innerHref = innerSecLink.getAttribute('href') || '';
              const innerId = innerHref.startsWith('#') ? innerHref.substring(1) : '';
              if (innerId && onLinkClick) {
                onLinkClick({ type: 'anchor', value: innerId });
                return;
              }
            }
          }
          scrollToElement(target);
        } else {
          scrollToElement(wrapper);
        }
      });
      return true;
    },
    getRawHtmlForAnchor: (anchorId) => {
      const escapedId = anchorId.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
      const idPattern = new RegExp(`<[^>]*\\b(?:id|name)=["']${escapedId}["']`, 'i');
      for (const [chunkId, rawHtml] of secureMemory.current.entries()) {
        if (rawHtml && idPattern.test(rawHtml)) return rawHtml;
      }
      return null;
    },
    // Scroll container to a pixel position
    scrollToPosition: (scrollTop) => {
      // contentRef is the outer scrollable div — we need to bubble up
      // Walk up from containerRef to find scrollable parent
      let el = containerRef.current?.parentElement;
      while (el) {
        if (el.scrollHeight > el.clientHeight) {
          el.scrollTop = scrollTop;
          return;
        }
        el = el.parentElement;
      }
    },
    // Imperatively append a new progressive chunk directly to avoid scroll jumps and reset.
    // This allows background loading to happen completely silently without re-mounting or refreshing the page.
    appendChunk: (nextHtml, chunkIndex, highlightsArr, observerRef) => {
      if (!shadowRootRef.current) return;
      const contentDiv = shadowRootRef.current.querySelector('.content');
      if (!contentDiv) return;

      const temp = document.createElement('div');
      temp.innerHTML = nextHtml;

      Array.from(temp.children).forEach((child, subIdx) => {
        const globalIdx = chunkIndex * 100 + subIdx; // Matches the server chunkSize of 100
        const chunkId = 'chunk_' + globalIdx;
        const outerHtml = child.outerHTML;
        const hasIframe = child.querySelector('iframe') || child.tagName === 'IFRAME';
        const hasTable = child.querySelector('table') || child.tagName === 'TABLE';

        secureMemory.current.set(chunkId, outerHtml);
        chunksMetadata.current.set(chunkId, { text: child.textContent || child.innerText || '' });

        const wrapper = document.createElement('div');
        wrapper.className = 'chunk-wrapper';
        wrapper.setAttribute('data-chunk-id', chunkId);

        if (hasIframe || hasTable) {
          wrapper.innerHTML = applyHighlightsToHtml(outerHtml, highlightsArr);
          wrapper.setAttribute('data-decrypted', 'true');
        } else {
          wrapper.setAttribute('data-decrypted', 'false');
          wrapper.innerHTML = '';
        }

        contentDiv.appendChild(wrapper);
        if (!hasIframe && !hasTable && observerRef) observerRef.observe(wrapper);
      });
    }
  }));

  useEffect(() => {
    if (!containerRef.current) return;
    if (!shadowRootRef.current) {
      shadowRootRef.current = containerRef.current.attachShadow({ mode: 'open' });
    }
    if (isWiped) {
      const msg = WIPE_MESSAGES[wipeReason] || 'Content hidden.';
      shadowRootRef.current.innerHTML = `<style>:host { 
  display: block;
  background: transparent;
}.content{line-height:1.9;font-family:Lora,Georgia,serif;color:inherit}</style><div class="content"><p style="color:#888;font-style:italic">${msg}</p></div>`;
      return;
    }
    if (!html) {
      shadowRootRef.current.innerHTML = `<style>:host { 
  display: block;
  background: transparent;
}.content{line-height:1.9;font-family:Lora,Georgia,serif;color:inherit}</style><div class="content"><p style="color:#888;font-style:italic">No content.</p></div>`;
      return;
    }
    shadowRootRef.current.innerHTML = `
      <style>
       :host { 
  display: block;
  background: transparent;
}
        .content { 
          line-height: 1.9; 
          font-family: Lora, Georgia, serif; 
          color: inherit;
          font-size: inherit;
          width: 100%;
          overflow-x: hidden;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
          background-image: ${buildWatermarkBackground(watermarkText)};
          background-repeat: repeat;
        }
        /* Defense-in-depth alongside the beforeprint JS wipe (see
           useContentProtection): even if print somehow fires before the wipe
           state has painted, the print stylesheet blanks the page. */
        @media print {
          .content { display: none !important; }
        }
        .content p {
          margin: 0 0 1em 0;
          word-break: break-word;
          overflow-wrap: anywhere;
          color: inherit !important;
          font-size: inherit !important;
        }
        .content div[id^="page"] {
          position: relative !important;
          background-color: #ffffff !important;
          box-shadow: none !important;
          border: none !important;
          margin: 0 auto !important;
        }
        .content div[id^="page"] p {
          position: absolute !important;
          white-space: pre !important;
          margin: 0 !important;
          color: #000000 !important;
        }
       .content li {
  color: inherit !important;
  font-size: inherit !important;
}
       .content td, .content th {
  color: inherit !important;
  font-size: inherit !important;
}
        .content h1, .content h2, .content h3, .content h4 { 
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 700; 
          margin: 1.2em 0 0.5em 0;
          line-height: 1.3;
        }
     .content ul { 
  padding-left: 1.5em; 
  margin: 0.5em 0 1em 0; 
}
.content ol { 
  padding-left: 2.8em;   /* extra space for 2-digit numbers */
  margin: 0.5em 0 1em 0; 
}
.content li { margin-bottom: 0.4em; color: inherit !important; font-size: inherit !important; }
          .content a {
  color: #1a6fc4;
  text-decoration: underline;
  cursor: pointer;
  word-break: break-all;
  overflow-wrap: anywhere;
  white-space: normal;
}
.content a:hover { 
  color: #0e4a8a; 
  background: rgba(26, 111, 196, 0.08);
  border-radius: 2px;
}
        .content li { margin-bottom: 0.4em; }
        /* Table fix — no overflow */
        .content table { 
          width: 100% !important;
          max-width: 100% !important;
          border-collapse: collapse; 
          margin: 16px 0;
          font-size: 0.85em;
          font-family: Nunito, sans-serif;
        }
        .content td, .content th { 
          border: 1px solid #ddd; 
          padding: 8px 10px; 
          text-align: left;
          vertical-align: top;
          word-wrap: break-word;
          overflow-wrap: break-word;
          max-width: 300px;
        }
        .content th { 
          background: #f5f5f5; 
          font-weight: 700; 
        }
        .content tr:nth-child(even) { background: #fafafa; }
        /* iframe — wrap in responsive container */
       .content iframe { 
  max-width: 100% !important;
  width: 100% !important;
  border: none; 
  border-radius: 8px;
  display: block;
  margin: 12px 0;
  aspect-ratio: 16/9;
  background: white;
}
        /* Image fix */
        .content img { 
          max-width: 100% !important;
          height: auto; 
          border-radius: 4px;
          margin: 8px 0;
          display: block;
        }
          .content a { color: #1a6fc4; text-decoration: underline; cursor: pointer; }
.content a:hover { color: #0e4a8a; }
        mark { border-radius: 2px; padding: 0 2px; }
        .search-temp-highlight { transition: all 0.3s ease; }
        span[data-xlink] { color: #C97B77; text-decoration: underline dotted; cursor: pointer; font-weight: 600; }
        .superScript { color: #d32f2f !important; font-size: 0.75em !important; vertical-align: super !important; cursor: pointer !important; font-weight: 700 !important; margin-left: 1px; }
        .superScript:hover { background: rgba(211,47,47,0.12); border-radius: 3px; padding: 0 3px; text-decoration: underline dotted; }
        sup {
          font-family: 'Source Sans 3', sans-serif;
          font-size: 9.5px !important;
          font-weight: 700 !important;
          color: #8b2020 !important;
          vertical-align: super !important;
          line-height: 0 !important;
          cursor: pointer !important;
        }
        sup:hover {
          background: rgba(139, 32, 32, 0.12) !important;
          border-radius: 3px !important;
          padding: 0 3px !important;
          text-decoration: underline dotted !important;
        }
        sup.am {
          font-family: 'Source Sans 3', sans-serif;
          font-size: 9px !important;
          font-weight: 700 !important;
          color: #8b6914 !important;
          vertical-align: super !important;
          line-height: 0 !important;
        }
        sup.fn-ref {
          color: #8b2020 !important;
          font-size: 10.5px !important;
          font-family: 'Source Sans 3', sans-serif;
          font-weight: 700 !important;
          cursor: pointer !important;
          position: relative;
          padding: 0 2px !important;
          transition: color 0.15s;
          vertical-align: super !important;
          line-height: 0 !important;
          border-radius: 2px !important;
        }
        sup.fn-ref:hover {
          color: #fff !important;
          background: #8b2020 !important;
        }
        .footnote-target { transition: background 0.8s ease; border-radius: 4px; padding: 2px 4px; }
        .footnote-highlight { background: rgba(201,123,119,0.25) !important; }
        .chunk-wrapper { transition: opacity 0.3s; }

         /* (tooltip rendered outside shadow DOM via React state) */
        .tt-active {
          color: #fff !important;
          background: #8b2020 !important;
        }

        /* Dynamic 2-column comparative layout */
        .sec-title-row {
          display: flex;
          gap: 24px;
          border-bottom: 2px solid #8b2020;
          padding-bottom: 6px;
          margin-bottom: 12px;
          margin-top: 20px;
        }
        .sec-title-left, .sec-title-right {
          flex: 1;
          min-width: 0;
        }
        .col-label {
          font-family: 'Nunito', sans-serif;
          font-size: 10px;
          font-weight: 700;
          color: #c97b77;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: block;
          margin-bottom: 2px;
        }
        .two-col {
          display: flex;
          gap: 24px;
          margin-bottom: 20px;
        }
        .col-left, .col-right {
          flex: 1;
          min-width: 0;
        }
        
        /* Mobile/Stacked view */
        @media (max-width: 768px) {
          .section-block {
            display: flex;
            flex-direction: column;
          }
          .sec-title-row {
            display: contents;
          }
          .sec-body {
            display: contents;
          }
          .two-col {
            display: contents;
          }
          .sec-title-left {
            order: 1;
            display: block;
            border-bottom: 1px solid #8b2020;
            padding-bottom: 4px;
            margin-bottom: 8px;
            margin-top: 16px;
          }
          .col-left {
            order: 2;
            display: block;
            margin-bottom: 16px;
          }
          .sec-title-right {
            order: 3;
            display: block;
            border-bottom: 1px solid #8b2020;
            padding-bottom: 4px;
            margin-bottom: 8px;
            margin-top: 16px;
          }
          .col-right {
            order: 4;
            display: block;
            margin-bottom: 16px;
          }
        }
      </style>
      <div class="content"></div>
    `;
    const contentDiv = shadowRootRef.current.querySelector('.content');
    secureMemory.current.clear();
    chunksMetadata.current.clear();

    let activeSup = null;

    if (disableLazy) {
      const decrypted = decrypt(html);
      const decryptedFn = decrypt(footnoteHtml);
      contentDiv.innerHTML = applyHighlightsToHtml(decrypted, highlightsRef.current) || '<p style="color:#888;font-style:italic">No content.</p>';
      preprocessSynopsis(contentDiv);

      // ── FOOTNOTE COLLECTION SCAN IN MEMORY FOR disableLazy ──
      const scanDiv = document.createElement('div');
      scanDiv.innerHTML = (decrypted || '') + (decryptedFn || '');
      const { fnMap, secNoteMap } = parseFootnotes(scanDiv);

      // ── SUPERSCRIPT TAGGING SCAN inside contentDiv ──
      const chapTitle = chapterTitle || '';
      const { alphaSecMap } = tagSuperscripts(contentDiv, secNoteMap, fnMap, chapTitle);
      // Per-page pass: override wrong global fnMap values with page-local footnote text.
      tagPerPageFootnotes(contentDiv);

      const clickHandler = (e) => {
        const target = e.target;

        const synId = target.getAttribute('data-synopsis-id') || target.closest('[data-synopsis-id]')?.getAttribute('data-synopsis-id');
        if (synId) {
          e.preventDefault();
          const targetId = `syn_target_${synId.split('_')[1]}`;
          const targetEl = contentDiv.querySelector(`#${targetId}`);
          if (targetEl) targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        const backToId = target.getAttribute('data-back-to') || target.closest('[data-back-to]')?.getAttribute('data-back-to');
        if (backToId) {
          e.preventDefault();
          const synEl = contentDiv.querySelector(`[data-synopsis-id="${backToId}"]`);
          if (synEl) {
            synEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            synEl.style.backgroundColor = 'rgba(201, 123, 119, 0.2)';
            setTimeout(() => { synEl.style.backgroundColor = ''; }, 1500);
          }
          return;
        }

        const xlink = target.dataset?.xlink || target.closest?.('[data-xlink]')?.dataset?.xlink;
        if (xlink) { onLinkClick?.({ type: 'xlink', value: xlink }); return; }

        const superEl = target.tagName === 'SUP' || target.classList.contains('superScript') || target.classList.contains('fn-ref')
          ? target
          : target.closest?.('sup, .superScript, .fn-ref');

        if (superEl) {
          if (activeSup) activeSup.classList.remove('tt-active');
          activeSup = superEl;
          superEl.classList.add('tt-active');
          const res = resolveSupData(superEl, secNoteMap, alphaSecMap, fnMap);
          onLinkClick?.({
            type: 'footnote',
            value: superEl.textContent.trim(),
            fnText: res.fnText,
            fnNote: res.fnNote,
            fnCite: res.fnCite,
            clientX: e.clientX,
            clientY: e.clientY,
          });
          return;
        }

        if (target.tagName === 'A' || target.closest('a')) {
          const aTag = target.tagName === 'A' ? target : target.closest('a');
          const href = aTag.getAttribute('href');
          if (href?.startsWith('http')) {
            e.preventDefault();
            window.open(href, '_blank');
          } else if (href?.startsWith('#')) {
            e.preventDefault();
            const targetId = href.substring(1);
            const el = contentDiv.querySelector(`#${CSS.escape(targetId)}`) || contentDiv.querySelector(`[id="${targetId}"]`);
            if (el) {
              let scrollParent = containerRef.current?.parentElement;
              while (scrollParent && scrollParent !== document.body && scrollParent !== document.documentElement) {
                if (scrollParent.scrollHeight > scrollParent.clientHeight) {
                  const overflowY = window.getComputedStyle(scrollParent).overflowY;
                  if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') break;
                }
                scrollParent = scrollParent.parentElement;
              }
              if (scrollParent && scrollParent.scrollHeight > scrollParent.clientHeight) {
                const targetRect = el.getBoundingClientRect();
                const parentRect = scrollParent.getBoundingClientRect();
                const offset = targetRect.top - parentRect.top + scrollParent.scrollTop;
                scrollParent.scrollTo({ top: Math.max(0, offset - 20), behavior: 'smooth' });
              } else {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            } else {
              onLinkClick?.({ type: 'anchor', value: targetId });
            }
          }
        }
      };

      const touchHandler = (e) => {
        const target = e.target;
        const sup = target.closest('sup, sup.fn-ref, .superScript');
        if (sup) {
          e.preventDefault();
          const t = e.touches[0];
          if (activeSup) activeSup.classList.remove('tt-active');
          activeSup = sup;
          sup.classList.add('tt-active');
          const res = resolveSupData(sup, secNoteMap, alphaSecMap, fnMap);
          onLinkClick?.({
            type: 'footnote',
            value: sup.textContent.trim(),
            fnText: res.fnText,
            fnNote: res.fnNote,
            fnCite: res.fnCite,
            clientX: t.clientX,
            clientY: t.clientY,
          });
        }
      };

      contentDiv.addEventListener('click', clickHandler);
      contentDiv.addEventListener('touchstart', touchHandler, { passive: false });

      return () => {
        contentDiv.removeEventListener('click', clickHandler);
        contentDiv.removeEventListener('touchstart', touchHandler);
      };
    }

    // PERF FIX: decrypt() runs CryptoJS.AES.decrypt() synchronously on the main
    // thread. For a full chapter chunk (up to ~100 pages) this alone can block
    // the tab for long enough to trigger "Page Unresponsive" on slow hardware.
    // decryptAsync() runs the same CryptoJS decrypt inside a Web Worker so the
    // main thread stays free to paint/respond to input while it runs. A
    // `cancelled` guard prevents a stale decrypt from a previous chapter/prop
    // change from clobbering DOM state after the effect has already re-run.
    let cancelled = false;
    let cleanupInner = () => { };

    (async () => {
      let fullDecrypted, fullFootnoteDecrypted;
      try {
        [fullDecrypted, fullFootnoteDecrypted] = await Promise.all([
          decryptAsync(html),
          decryptAsync(footnoteHtml),
        ]);
      } catch (err) {
        console.warn('[Decrypt] Worker decrypt failed, content will not render:', err);
        return;
      }
      if (cancelled) return; // chapter/prop changed again before decrypt finished — discard

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = (fullDecrypted || '') + (fullFootnoteDecrypted || '');

      // Initialize maps as empty refs first
      const fnMapRef = { current: {} };
      const secNoteMapRef = { current: {} };
      const alphaSecMapRef = { current: {} };
      const chapTitle = chapterTitle || '';

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const el = entry.target;
          const chunkId = el.getAttribute('data-chunk-id');
          if (entry.isIntersecting) {
            if (el.getAttribute('data-decrypted') !== 'true') {
              const chunkHtml = secureMemory.current.get(chunkId);
              el.innerHTML = applyHighlightsToHtml(chunkHtml, highlightsRef.current);
              el.setAttribute('data-decrypted', 'true');

              // Lazily process footnotes and superscripts ONLY for this visible chunk
              tagPerPageFootnotes(el);
              tagSuperscripts(el, secNoteMapRef.current, fnMapRef.current, chapTitle);
              // PERF FIX 2: Mark as tagged so the background re-scan pass skips this chunk
              el.setAttribute('data-footnotes-tagged', 'true');

              // Fallback for sups not inside a page-div
              el.querySelectorAll('sup, .superScript, .fn-ref').forEach(sup => {
                if (!sup.dataset.text) {
                  const citeNum = parseInt(sup.textContent.trim().match(/\d+/)?.[0] || '0', 10);
                  if (citeNum && fnMapRef.current[citeNum]) sup.dataset.text = fnMapRef.current[citeNum];
                }
              });
            }
          } else {
            if (el.getAttribute('data-decrypted') === 'true') {
              if (el.querySelector('.search-temp-highlight')) return;
              const rect = el.getBoundingClientRect();
              if (rect.height > 0) el.style.minHeight = rect.height + 'px';
              el.innerHTML = '';
              el.setAttribute('data-decrypted', 'false');
              el.removeAttribute('data-footnotes-tagged');
            }
          }
        });
        // PERF FIX 4: Use fixed 200px buffer instead of 50% screen height.
        // 50% could pre-render 3-4 chunks simultaneously during fast scroll,
        // causing burst render spikes. 200px pre-renders only the next chunk — plenty.
      }, { root: null, rootMargin: '200px 0px' });

      // Expose observer instance so appendChunk can register progressive wrappers
      shadowRootRef.current.__progressiveObserver = observer;

      const children = Array.from(tempDiv.children);
      const batchSize = 15;
      let currentIndex = 0;

      const processNextBatch = () => {
        if (cancelled) {
          tempDiv.innerHTML = '';
          return;
        }
        const end = Math.min(currentIndex + batchSize, children.length);
        for (let i = currentIndex; i < end; i++) {
          if (cancelled) {
            tempDiv.innerHTML = '';
            return;
          }
          const child = children[i];
          const chunkId = 'chunk_' + i;
          // PERF FIX 3: Store innerHTML directly — avoids the expensive DOM→string
          // serialization that child.outerHTML triggers for every chunk on load.
          // The chunk-wrapper div is rebuilt anyway; we only need the inner content.
          const innerHtml = child.outerHTML;
          const hasIframe = child.querySelector('iframe') || child.tagName === 'IFRAME';
          const hasTable = child.querySelector('table') || child.tagName === 'TABLE';

          // Store raw HTML to bypass high CPU encryption overhead
          secureMemory.current.set(chunkId, innerHtml);
          chunksMetadata.current.set(chunkId, { text: child.textContent || child.innerText || '' });

          const wrapper = document.createElement('div');
          wrapper.className = 'chunk-wrapper';
          wrapper.setAttribute('data-chunk-id', chunkId);

          if (hasIframe || hasTable) {
            wrapper.innerHTML = applyHighlightsToHtml(innerHtml, highlightsRef.current);
            wrapper.setAttribute('data-decrypted', 'true');
          } else {
            wrapper.setAttribute('data-decrypted', 'false');
            wrapper.innerHTML = '';
          }

          contentDiv.appendChild(wrapper);
          if (!hasIframe && !hasTable) observer.observe(wrapper);
        }

        currentIndex = end;
        if (currentIndex < children.length) {
          setTimeout(processNextBatch, 0); // yields to thread for painting
        } else {
          // PERF FIX: this used to be ONE setTimeout callback doing
          // parseFootnotes + preprocessSynopsis + tagSuperscripts +
          // re-tagging already-visible chunks, all back-to-back with no yield
          // between them. For a large chapter (hundreds of pages, thousands
          // of footnote refs/superscripts) each of those is itself a
          // full-document querySelectorAll + forEach pass — chained together
          // synchronously they add up to one long unbroken task, which is
          // exactly what trips "Page Unresponsive", especially if the user is
          // also clicking around elsewhere while it runs. Splitting into
          // separate setTimeout(...,0) steps — each checking `cancelled` in
          // case the user has since switched chapters — keeps every
          // individual task short while still finishing in roughly the same
          // total wall-clock time.
          setTimeout(() => {
            if (cancelled) return;
            let parsed;
            try {
              parsed = parseFootnotes(tempDiv);
            } catch (err) {
              console.warn('[LazyParse] parseFootnotes failed:', err);
              tempDiv.innerHTML = '';
              return;
            }
            fnMapRef.current = parsed.fnMap;
            secNoteMapRef.current = parsed.secNoteMap;

            setTimeout(() => {
              if (cancelled) { tempDiv.innerHTML = ''; return; }
              try {
                preprocessSynopsis(tempDiv);
              } catch (err) {
                console.warn('[LazyParse] preprocessSynopsis failed:', err);
              }

              setTimeout(() => {
                if (cancelled) { tempDiv.innerHTML = ''; return; }
                try {
                  const tagged = tagSuperscripts(tempDiv, secNoteMapRef.current, fnMapRef.current, chapTitle);
                  alphaSecMapRef.current = tagged.alphaSecMap;
                } catch (err) {
                  console.warn('[LazyParse] tagSuperscripts failed:', err);
                }

                setTimeout(() => {
                  if (!cancelled) {
                    try {
                      // PERF FIX 2: Skip chunks that were already tagged during lazy render
                      // (they already have correct footnote/superscript data).
                      // Only re-tag chunks that became visible BEFORE parseFootnotes finished,
                      // identified by missing data-footnotes-tagged attribute.
                      const rendered = contentDiv.querySelectorAll('.chunk-wrapper[data-decrypted="true"]');
                      rendered.forEach(el => {
                        if (el.getAttribute('data-footnotes-tagged') === 'true') return;
                        tagPerPageFootnotes(el);
                        tagSuperscripts(el, secNoteMapRef.current, fnMapRef.current, chapTitle);
                        el.setAttribute('data-footnotes-tagged', 'true');
                      });
                    } catch (err) {
                      console.warn('[LazyParse] Re-tagging visible chunks failed:', err);
                    }
                  }
                  tempDiv.innerHTML = '';
                }, 0);
              }, 0);
            }, 0);
          }, 150);
        }
      };

      processNextBatch();

      const clickHandler = (e) => {
        const target = e.target;

        const synId = target.getAttribute('data-synopsis-id') || target.closest('[data-synopsis-id]')?.getAttribute('data-synopsis-id');
        if (synId) {
          e.preventDefault();
          const targetId = `syn_target_${synId.split('_')[1]}`;
          let targetEl = contentDiv.querySelector(`#${targetId}`);
          if (!targetEl) {
            const wrappers = Array.from(contentDiv.querySelectorAll('.chunk-wrapper'));
            const targetWrapper = wrappers.find(w => {
              const chunkId = w.getAttribute('data-chunk-id');
              const rawHtml = secureMemory.current.get(chunkId);
              return rawHtml && (rawHtml.includes(`id="${targetId}"`) || rawHtml.includes(`id='${targetId}'`));
            });
            if (targetWrapper) {
              const chunkId = targetWrapper.getAttribute('data-chunk-id');
              const chunkHtml = secureMemory.current.get(chunkId);
              targetWrapper.innerHTML = applyHighlightsToHtml(chunkHtml, highlightsRef.current);
              targetWrapper.setAttribute('data-decrypted', 'true');
              targetEl = contentDiv.querySelector(`#${targetId}`);
            }
          }
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }

        const backToId = target.getAttribute('data-back-to') || target.closest('[data-back-to]')?.getAttribute('data-back-to');
        if (backToId) {
          e.preventDefault();
          let synEl = contentDiv.querySelector(`[data-synopsis-id="${backToId}"]`);
          if (!synEl) {
            const wrappers = Array.from(contentDiv.querySelectorAll('.chunk-wrapper'));
            const synWrapper = wrappers.find(w => {
              const chunkId = w.getAttribute('data-chunk-id');
              const rawHtml = secureMemory.current.get(chunkId);
              return rawHtml && (rawHtml.includes(`data-synopsis-id="${backToId}"`) || rawHtml.includes(`data-synopsis-id='${backToId}'`));
            });
            if (synWrapper) {
              const chunkId = synWrapper.getAttribute('data-chunk-id');
              const chunkHtml = secureMemory.current.get(chunkId);
              synWrapper.innerHTML = applyHighlightsToHtml(chunkHtml, highlightsRef.current);
              synWrapper.setAttribute('data-decrypted', 'true');
              synEl = contentDiv.querySelector(`[data-synopsis-id="${backToId}"]`);
            }
          }
          if (synEl) {
            synEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            synEl.style.backgroundColor = 'rgba(201, 123, 119, 0.2)';
            setTimeout(() => { synEl.style.backgroundColor = ''; }, 1500);
          }
          return;
        }
        const xlink = target.dataset?.xlink || target.closest?.('[data-xlink]')?.dataset?.xlink;
        if (xlink) { onLinkClick?.({ type: 'xlink', value: xlink }); return; }

        const superEl = target.tagName === 'SUP' || target.classList.contains('superScript') || target.classList.contains('fn-ref')
          ? target
          : target.closest?.('sup, .superScript, .fn-ref');

        if (superEl) {
          if (activeSup) activeSup.classList.remove('tt-active');
          activeSup = superEl;
          superEl.classList.add('tt-active');
          const res = resolveSupData(superEl, secNoteMapRef.current, alphaSecMapRef.current, fnMapRef.current);
          onLinkClick?.({
            type: 'footnote',
            value: superEl.textContent.trim(),
            fnText: res.fnText,
            fnNote: res.fnNote,
            fnCite: res.fnCite,
            clientX: e.clientX,
            clientY: e.clientY,
          });
          return;
        }

        if (target.tagName === 'A' || target.closest('a')) {
          const aTag = target.tagName === 'A' ? target : target.closest('a');
          const href = aTag.getAttribute('href');
          if (href?.startsWith('http')) {
            e.preventDefault();
            window.open(href, '_blank');
          } else if (href?.startsWith('#')) {
            e.preventDefault();
            const targetId = href.substring(1);
            let targetEl = contentDiv.querySelector(`#${CSS.escape(targetId)}`) || contentDiv.querySelector(`[id="${targetId}"]`);
            if (!targetEl) {
              const wrappers = Array.from(contentDiv.querySelectorAll('.chunk-wrapper'));
              const targetWrapper = wrappers.find(w => {
                const chunkId = w.getAttribute('data-chunk-id');
                const rawHtml = secureMemory.current.get(chunkId);
                return rawHtml && (rawHtml.includes(`id="${targetId}"`) || rawHtml.includes(`id='${targetId}'`));
              });
              if (targetWrapper) {
                const chunkId = targetWrapper.getAttribute('data-chunk-id');
                const chunkHtml = secureMemory.current.get(chunkId);
                targetWrapper.innerHTML = applyHighlightsToHtml(chunkHtml, highlightsRef.current);
                targetWrapper.setAttribute('data-decrypted', 'true');
                targetEl = contentDiv.querySelector(`#${CSS.escape(targetId)}`) || contentDiv.querySelector(`[id="${targetId}"]`);
              }
            }
            if (targetEl) {
              requestAnimationFrame(() => {
                scrollToElement(targetEl);
              });
            } else {
              onLinkClick?.({ type: 'anchor', value: targetId });
            }
          }
        }
      };

      const touchHandler = (e) => {
        const target = e.target;
        const sup = target.closest('sup, sup.fn-ref, .superScript');
        if (sup) {
          e.preventDefault();
          const t = e.touches[0];
          if (activeSup) activeSup.classList.remove('tt-active');
          activeSup = sup;
          sup.classList.add('tt-active');
          const res = resolveSupData(sup, secNoteMapRef.current, alphaSecMapRef.current, fnMapRef.current);
          onLinkClick?.({
            type: 'footnote',
            value: sup.textContent.trim(),
            fnText: res.fnText,
            fnNote: res.fnNote,
            fnCite: res.fnCite,
            clientX: t.clientX,
            clientY: t.clientY,
          });
        }
      };

      contentDiv.addEventListener('click', clickHandler);
      contentDiv.addEventListener('touchstart', touchHandler, { passive: false });

      cleanupInner = () => {
        contentDiv.removeEventListener('click', clickHandler);
        contentDiv.removeEventListener('touchstart', touchHandler);
        observer.disconnect();
        secureMemory.current.clear();
        chunksMetadata.current.clear();
      };
    })();

    // Synchronous top-level cleanup, required by React. If the decrypt above
    // hasn't resolved yet (e.g. the user flipped chapters again quickly),
    // `cancelled = true` makes the async block above bail out as soon as it
    // wakes up, and there's nothing else to tear down yet. If setup already
    // completed, delegate to the real cleanup (remove listeners, disconnect
    // the observer) exactly as before.
    return () => {
      cancelled = true;
      cleanupInner();
    };
  }, [html, footnoteHtml, isWiped, wipeReason, watermarkText, onLinkClick, disableLazy, chapterTitle]);

  // PERF FIX: highlights used to be a dependency of the effect above, so
  // adding a single annotation re-ran the ENTIRE mount effect — full
  // re-decrypt (via the Web Worker, but still real async work + a render
  // stall), full chunk rebuild, full footnote/synopsis re-scan. Highlights
  // only affect which spans get wrapped in <mark>, so instead we keep
  // highlightsRef current for any chunk that decrypts from here on (handled
  // above via highlightsRef.current), and separately re-stamp chunks that are
  // ALREADY rendered — a cheap, synchronous, per-chunk innerHTML swap using
  // the raw HTML already sitting in secureMemory, no decrypt involved.
  useEffect(() => {
    highlightsRef.current = highlights;
    const contentDiv = shadowRootRef.current?.querySelector('.content');
    if (!contentDiv) return;
    const rendered = contentDiv.querySelectorAll('.chunk-wrapper[data-decrypted="true"]');
    rendered.forEach(wrapper => {
      const chunkId = wrapper.getAttribute('data-chunk-id');
      const rawHtml = secureMemory.current.get(chunkId);
      if (rawHtml) wrapper.innerHTML = applyHighlightsToHtml(rawHtml, highlights);
    });
  }, [highlights]);

  return <div ref={containerRef} style={style} />;
});

// Memo — prevent re-render on noteText/selectedText changes
// NOTE: style (color + fontSize) IS included so theme changes and font-size
// slider changes correctly propagate into the Shadow DOM via CSS inheritance.
const MemoShadowContent = React.memo(ShadowContent, (prev, next) => {
  // FIX: this used to run JSON.stringify() on the highlights array on EVERY
  // single re-render of ReaderPage (toast popping up, toolbar moving, font
  // slider, etc. — all cause a re-render). `highlights` is already a stable
  // useMemo() reference upstream (only changes when annotations actually
  // change), so a plain reference check is correct and removes a lot of
  // needless stringify work on the main thread.
  return prev.html === next.html &&
    prev.footnoteHtml === next.footnoteHtml &&
    prev.chapterTitle === next.chapterTitle &&
    prev.isWiped === next.isWiped &&
    prev.wipeReason === next.wipeReason &&
    prev.watermarkText === next.watermarkText &&
    prev.highlights === next.highlights &&
    prev.disableLazy === next.disableLazy &&
    prev.chunkDownloaderRef === next.chunkDownloaderRef &&
    prev.style?.color === next.style?.color &&
    prev.style?.fontSize === next.style?.fontSize;
});

const THEMES = {
  light: { bg: '#FDFAF7', text: '#2C2C35', subText: '#888899', border: '#E8E4DF', toolbar: '#FFFFFF', name: '☀ Light' },
  sepia: { bg: '#F7F0E4', text: '#3A2E1E', subText: '#7A6A5A', border: '#DED4C0', toolbar: '#F2EAD8', name: '🌤 Sepia' },
  dark: { bg: '#1E1E28', text: '#D4CCC0', subText: '#888899', border: '#3A3A4A', toolbar: '#252236', name: '🌙 Dark' },
};

const HL_COLORS = [
  { name: 'yellow', bg: '#FBF3D0', label: 'Yellow' },
  { name: 'red', bg: '#FAE8E6', label: 'Red' },
  { name: 'green', bg: '#E4F4EC', label: 'Green' },
  { name: 'blue', bg: '#EEF2F8', label: 'Blue' },
  { name: 'purple', bg: '#F0EDF8', label: 'Purple' },
];

// FIX: must be a stable reference — an inline `[]` written directly in JSX
// creates a brand-new array every render, which would defeat the
// reference-equality check in MemoShadowContent above and force a full
// (expensive) content rebuild on every single re-render.
const EMPTY_HIGHLIGHTS = [];

// PDFStudioSidebarTree — renders ONLY the sub-items for a single chapter.
// `subHtml` is the innerHTML of the gc-sub block belonging to this chapter.
// The converted HTML has 3 levels of collapsible subs:
//   gc-group   → gc-sub     (top level)
//   dt-l1-group → dt-l1-sub (section level, e.g. PRELIMINARY)
//   dt-l2-group → dt-l2-sub (topic level, e.g. Definitions)
// This component mirrors that exact structure.
const PDFStudioSidebarTree = React.memo(({ subHtml, onSelectAnchor, textColor, pendingAnchorId }) => {
  const treeRef = useRef(null);

  useEffect(() => {
    if (!treeRef.current || !subHtml) return;

    try {
      treeRef.current.innerHTML = subHtml;

      // All groups start collapsed — user clicks to expand
      treeRef.current
        .querySelectorAll('.gc-group, .dt-l1-group, .dt-l2-group, .dt-syn-group')
        .forEach(g => g.classList.remove('expanded'));

      treeRef.current.querySelectorAll('a').forEach(a => {
        a.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // ONLY toggle if THIS link is the group-title toggle (has .has-children class).
          // Leaf links like dt-l2-lnk-direct, dt-l3-lnk must always navigate — never toggle.
          if (a.classList.contains('has-children')) {
            const group = a.closest('.gc-group, .dt-l1-group, .dt-l2-group, .dt-syn-group');
            if (group) {
              let subSel = null;
              if (group.classList.contains('gc-group')) subSel = ':scope > .gc-sub';
              if (group.classList.contains('dt-l1-group')) subSel = ':scope > .dt-l1-sub';
              if (group.classList.contains('dt-l2-group')) subSel = ':scope > .dt-l2-sub';
              if (group.classList.contains('dt-syn-group')) subSel = ':scope > .dt-syn-sub';
              if (subSel && group.querySelector(subSel)) {
                group.classList.toggle('expanded');
              }
            }
          }

          // All links → navigate to the anchor
          const href = a.getAttribute('href');
          if (href && href.startsWith('#')) {
            treeRef.current?.querySelectorAll('a.air-toc-loading').forEach(el => el.classList.remove('air-toc-loading'));
            a.classList.add('air-toc-loading');
            onSelectAnchor(href.substring(1), (a.textContent || '').trim());
          }
        });
      });
    } catch (err) {
      console.warn('[PDFStudioSidebarTree] Error building sidebar tree:', err);
    }
  }, [subHtml, onSelectAnchor]);

  useEffect(() => {
    if (!treeRef.current) return;
    if (pendingAnchorId) return; // still loading — leave whatever's marked as-is
    treeRef.current.querySelectorAll('a.air-toc-loading').forEach(el => el.classList.remove('air-toc-loading'));
  }, [pendingAnchorId]);

  return (
    <div className="pdf-sidebar-tree-wrapper">
      <style>{`
        .pdf-sidebar-tree-wrapper {
          padding: 2px 8px 10px 0;
          font-family: 'Nunito', sans-serif;
          font-size: 11px;
          border-left: 2px solid rgba(201,123,119,0.25);
          margin: 0 4px 4px 18px;
        }

        /* ── Hide sidebar chrome — not needed here ── */
        .pdf-sidebar-tree-wrapper .sb-header,
        .pdf-sidebar-tree-wrapper .sb-actions,
        .pdf-sidebar-tree-wrapper .sb-close-btn { display: none !important; }

        /* ── All sub-lists hidden by default ── */
        .pdf-sidebar-tree-wrapper .gc-sub,
        .pdf-sidebar-tree-wrapper .dt-l1-sub,
        .pdf-sidebar-tree-wrapper .dt-l2-sub,
        .pdf-sidebar-tree-wrapper .dt-syn-sub {
          display: none;
        }

        /* ── Show sub-list when parent group is expanded ── */
        .pdf-sidebar-tree-wrapper .gc-group.expanded    > .gc-sub,
        .pdf-sidebar-tree-wrapper .dt-l1-group.expanded > .dt-l1-sub,
        .pdf-sidebar-tree-wrapper .dt-l2-group.expanded > .dt-l2-sub,
        .pdf-sidebar-tree-wrapper .dt-syn-group.expanded > .dt-syn-sub {
          display: block;
        }

        /* ── Indentation per level ── */
        .pdf-sidebar-tree-wrapper .gc-sub    { padding-left: 0; }
        .pdf-sidebar-tree-wrapper .dt-l1-sub { padding-left: 10px; border-left: 1px solid rgba(201,123,119,0.15); margin: 2px 0 2px 6px; }
        .pdf-sidebar-tree-wrapper .dt-l2-sub { padding-left: 10px; border-left: 1px solid rgba(201,123,119,0.10); margin: 2px 0 2px 6px; }
        .pdf-sidebar-tree-wrapper .dt-syn-sub { padding-left: 10px; border-left: 1px solid rgba(201,123,119,0.10); margin: 2px 0 2px 6px; }

        /* ── Base link style ── */
        .pdf-sidebar-tree-wrapper a {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 3px 8px 3px 8px;
          color: ${textColor || 'inherit'};
          text-decoration: none;
          line-height: 1.4;
          border-radius: 4px;
          font-size: 10.5px;
          cursor: pointer;
          gap: 4px;
        }
        .pdf-sidebar-tree-wrapper a:hover {
          background: rgba(201,123,119,0.10);
          color: var(--rose, #C97B77);
        }

        /* ── L1 section headers (PRELIMINARY, PART I, etc.) ── */
        .pdf-sidebar-tree-wrapper a.dt-l1-lnk,
        .pdf-sidebar-tree-wrapper a.dt-l1-lnk-direct {
          font-weight: 700;
          font-size: 10.5px;
          color: ${textColor || 'inherit'};
          padding-left: 8px;
          margin-top: 3px;
        }

        /* ── L2 topic links ── */
        .pdf-sidebar-tree-wrapper a.dt-l2-lnk,
        .pdf-sidebar-tree-wrapper a.dt-l2-lnk-direct,
        .pdf-sidebar-tree-wrapper a.dt-syn-lnk {
          font-weight: 400;
          font-size: 10px;
          color: ${textColor || 'inherit'};
          opacity: 0.85;
        }

        /* ── L3 sub-topic links ── */
        .pdf-sidebar-tree-wrapper a.dt-l3-lnk {
          font-size: 9.5px;
          opacity: 0.75;
          padding-left: 10px;
        }

        /* ── +/− indicator for expandable items ── */
        .pdf-sidebar-tree-wrapper .gc-group > a.has-children::after,
        .pdf-sidebar-tree-wrapper .dt-l1-group > a.has-children::after,
        .pdf-sidebar-tree-wrapper .dt-l2-group > a.has-children::after,
        .pdf-sidebar-tree-wrapper .dt-syn-group > a.has-children::after {
          content: '+';
          flex-shrink: 0;
          color: rgba(201,123,119,0.65);
          font-weight: 700;
          font-size: 11px;
          line-height: 1;
          margin-left: auto;
          padding-left: 4px;
        }
        .pdf-sidebar-tree-wrapper .gc-group.expanded    > a.has-children::after,
        .pdf-sidebar-tree-wrapper .dt-l1-group.expanded > a.has-children::after,
        .pdf-sidebar-tree-wrapper .dt-l2-group.expanded > a.has-children::after,
        .pdf-sidebar-tree-wrapper .dt-syn-group.expanded > a.has-children::after {
          content: '−';
        }

        /* ── Group spacing ── */
        .pdf-sidebar-tree-wrapper .dt-l1-group { margin-bottom: 1px; }
        .pdf-sidebar-tree-wrapper .dt-l2-group { margin-bottom: 1px; }
        .pdf-sidebar-tree-wrapper .dt-l2-group-direct { margin-bottom: 0; }

        /* ── Pending/loading state — applied the instant a leaf link is
           clicked, so the user gets feedback before any network/decrypt
           work has even started, instead of a silent gap that reads as a
           frozen screen and invites repeat clicking. Cleared centrally by
           ReaderPage once navigation actually completes or fails. ── */
        .pdf-sidebar-tree-wrapper a.air-toc-loading {
          color: var(--rose, #C97B77) !important;
          opacity: 1 !important;
        }
        .pdf-sidebar-tree-wrapper a.air-toc-loading::after {
          content: '';
          flex-shrink: 0;
          width: 9px;
          height: 9px;
          margin-left: auto;
          border: 1.5px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: air-toc-spin 0.6s linear infinite;
        }
        @keyframes air-toc-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div ref={treeRef} />
    </div>
  );
});

const OldAndPdfStudioReaderPage = ({ book, onBack, onOpenBook }) => {
  const { getToken } = useApp();
  const { readingTheme, setReadingTheme, fontSize, setFontSize, addAnnotation, toggleBookmark, bookmarks, saveProgress, currentUser, annotations } = useApp();

  // URL params — component ke andar rakho taaki fresh URL mile
  const initialChapterId = useMemo(() => new URLSearchParams(window.location.search).get('chapter'), []);
  const initialHighlight = useMemo(() => new URLSearchParams(window.location.search).get('highlight'), []);
  const initialScrollTop = useMemo(() => parseInt(new URLSearchParams(window.location.search).get('scrollTop') || '0'), []);
  const t = THEMES[readingTheme] || THEMES.light;

  const [chapters, setChapters] = useState([]);
  const [sidebarHtml, setSidebarHtml] = useState(null);
  const [expandedSubIds, setExpandedSubIds] = useState(new Set());
  const [activeChapter, setActive] = useState(null);
  const [chapterData, setChapterData] = useState(null);
  const [loadingCh, setLoadingCh] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [chPage, setChPage] = useState(1);
  const [chPag, setChPag] = useState(null);
  const [totalCh, setTotalCh] = useState(0);
  const [accessError, setAccessError] = useState(null);
  const [showToc, setShowToc] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [showAnnot, setShowAnnot] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [inSearch, setInSearch] = useState('');
  const [searchHits, setSearchHits] = useState([]);
  const [selectedText, setSelected] = useState('');
  const [noteText, setNoteText] = useState('');
  const [showXlink, setShowXlink] = useState(false);
  const [xlinkTerm, setXlinkTerm] = useState('');
  const [xlinkResults, setXlinkResults] = useState([]);
  const [xlinkLoading, setXlinkLoading] = useState(false);
  const [toolbarPos, setToolbarPos] = useState({ show: false, top: 0, left: 0 });
  const [toast, setToast] = useState(null);
  // CONTENT PROTECTION: this used to be `const [isWiped, setIsWiped] =
  // useState(false)` with setIsWiped never actually called anywhere — the
  // "Security wipe: window inactive" UI already existed in ShadowContent but
  // nothing ever triggered it. useContentProtection wires it up for real:
  // blur/tab-hidden, a best-effort DevTools-open heuristic, and print. See
  // src/utils/contentProtection.js for exactly what this can and can't do —
  // it's an honest deterrent + attribution layer (watermark), not a claim
  // that content can be made uncopyable, since no web technology can do that.
  const { isWiped, wipeReason, watermarkText } = useContentProtection({
    userLabel: currentUser?.email || currentUser?.name || currentUser?.username || null,
  });
  const [fnTooltip, setFnTooltip] = useState(null); // { fnText, fnNote, fnCite, clientX, clientY }
  const [progressChunkLoaded, setProgressChunkLoaded] = useState(0);
  const [totalChapterChunks, setTotalChapterChunks] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(null);
  // Drives the instant per-link loading indicator in PDFStudioSidebarTree.
  // Set the moment a sidebar leaf link is clicked, cleared on every exit
  // path of handleAnchorClick (success, failure, timeout, or superseded by
  // a newer click) — see that function below.
  const [pendingAnchorId, setPendingAnchorId] = useState(null);
  // Same idea as pendingAnchorId, but for the top-level chapter buttons
  // (which don't go through handleAnchorClick) — set instantly on click,
  // cleared once the debounced loadChapter() actually starts.
  const [pendingChapterId, setPendingChapterId] = useState(null);

  const contentRef = useRef(null);
  const shadowContentRef = useRef(null);
  const footnoteRef = useRef(null);
  // CRASH FIX: these two used to be window.__activeChunkDownloader /
  // window.__activeChunkDownloaderStatus — plain globals shared across the
  // whole page. Every loadChapter() call overwrote them with a fresh
  // closure, but never cancelled whatever the PREVIOUS chapter's closure
  // was still doing. If the user clicked a second (or third, fourth...)
  // sidebar link before the first chapter finished loading, each click
  // spun up its own progressive-chunk background loop — decrypting via the
  // Worker and calling shadowContentRef.current.appendChunk() — and NONE of
  // them ever stopped, because nothing was cancelling them. With enough
  // stacked, orphaned loops all still trying to decrypt and append content
  // into a shadow root that had since been rebuilt for a completely
  // different chapter, this is what produced the crash on rapid clicking.
  // Refs (rather than component state) because these are read/written from
  // inside plain closures on a hot path, not something that should trigger
  // a re-render. chapterLoadSeqRef is the actual fix: every loadChapter()
  // call gets its own sequence number, and every async continuation checks
  // it's still current before touching state or the DOM — see loadChapter
  // below.
  const chunkDownloaderRef = useRef(null);
  const chunkDownloaderStatusRef = useRef(null);
  const chapterLoadSeqRef = useRef(0);
  // LAG FIX: the sequence guard above stops rapid clicks from corrupting
  // state or piling up orphaned background loops, but it doesn't stop each
  // click from STARTING real work first — a network fetch, a decrypt-worker
  // round trip, and a cascade of ~6 React state updates (setActive,
  // setLoadingContent, setExpandedSubIds, etc.), all of which still run
  // before the stale result is discarded. Click 10 sidebar links in two
  // seconds and that's 10 fetches + 10 state-update cascades + 10
  // re-renders of the whole reader, even though only the last one's result
  // ever gets used — that's the lag, and enough of it in a row is what
  // tips into "unresponsive". navDebounceRef fixes the actual cause: only
  // the last click within a short quiet window (see NAV_DEBOUNCE_MS below)
  // actually triggers navigation at all. The per-link spinner (see
  // pendingAnchorId) stays instant and un-debounced — only the expensive
  // part waits.
  const navDebounceRef = useRef(null);
  const activeProgressIntervalRef = useRef(null);
  // PERF FIX 5: Sequence counter to cancel stale navigateWithRetry poll loops.
  // Without this, every navigation call (even spaced minutes apart) leaves 20×150ms
  // poll timers running in the background. After 5+ minutes of reading, dozens of
  // dead loops fire simultaneously and saturate the main thread event queue.
  const navRetrySeqRef = useRef(0);
  const bookId = book?._id || book?.id;
  const isBookmarked = bookmarks.includes(String(bookId));
  const progress = currentUser?.progress?.[String(bookId)]?.percent || 0;

  const showToast = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); }, []);

  const highlights = useMemo(() => {
    const map = {};
    annotations
      .filter(a => a.type === 'highlight' && String(a.bookId) === String(bookId))
      .forEach(a => {
        const key = String(a.chapterId);
        if (!map[key]) map[key] = [];
        map[key].push({ id: String(a._id), text: a.selectedText || '', color: a.color || 'yellow' });
      });
    return map;
  }, [annotations, bookId]);

  // PERF OPTIMIZATION: Parse sidebarHtml ONCE when fetched into memoized structures.
  // This eliminates re-parsing multi-megabyte HTML strings on every render or click,
  // completely preventing Chrome "Pages Unresponsive" thread freezes on client devices.
  const { subMap, pageMap } = useMemo(() => {
    if (!sidebarHtml) return { subMap: {}, pageMap: [] };
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sidebarHtml, 'text/html');

      const sMap = {};
      const sidebarEl = doc.querySelector('aside.sidebar, nav') || doc.body;
      sidebarEl.querySelectorAll('.gc-group').forEach(group => {
        const lnk = group.querySelector(':scope > a.gc-lnk, :scope > div > a.gc-lnk');
        const sub = group.querySelector(':scope > .gc-sub, :scope > div > .gc-sub');
        if (lnk && sub) {
          const label = (lnk.textContent || '').trim().toLowerCase();
          sMap[label] = sub.innerHTML;
        }
      });

      const pMap = [];
      if (chapters && chapters.length > 0) {
        const gcLinks = doc.querySelectorAll('a.gc-lnk');
        gcLinks.forEach(lnk => {
          const href = lnk.getAttribute('href') || '';
          const pNum = parseInt(href.replace(/\D/g, ''), 10);
          const titleRaw = (lnk.textContent || '').trim();
          const titleNorm = titleRaw.toLowerCase().replace(/&amp;/g, '&').replace(/\s+/g, ' ');
          if (!isNaN(pNum) && titleNorm) {
            const matchedCh = chapters.find(c => {
              const cTitle = (c.chapter_title || c.name || '').toLowerCase().replace(/&amp;/g, '&').replace(/\s+/g, ' ');
              return cTitle === titleNorm || cTitle.includes(titleNorm) || titleNorm.includes(cTitle);
            });
            if (matchedCh) {
              pMap.push({ startPage: pNum, ch: matchedCh });
            }
          }
        });
        pMap.sort((a, b) => a.startPage - b.startPage);
      }

      return { subMap: sMap, pageMap: pMap };
    } catch (err) {
      console.warn('[sidebarHtmlMemo] Failed to parse sidebarHtml:', err);
      return { subMap: {}, pageMap: [] };
    }
  }, [sidebarHtml, chapters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!bookId) return;
    setChapters([]); setActive(null); setChapterData(null); setAccessError(null);
    setSidebarHtml(null); // Clear previous TOC

    (async () => {
      setLoadingCh(true);
      try {
        const token = await getToken();
        // 1. Fetch chapters instantly (since sidebar_html is now null in this API response)
        const res = await booksAPI.getChapters(token, bookId, { page: chPage, limit: 50 });
        const chs = (res.data || []).map(ch => ({
          ...ch,
          chapter_title: decrypt(ch.chapter_title) || ch.name || '',
          name: decrypt(ch.name) || decrypt(ch.chapter_title) || ch.name || ''
        }));
        setChapters(chs);
        setChPag(res.pagination);
        setTotalCh(res.pagination?.total || 0);

        if (chs.length > 0) {
          const targetCh = initialChapterId
            ? chs.find(c => String(c.content_id) === String(initialChapterId)) || chs[0]
            : chs[0];
          loadChapter(targetCh, chs, res.pagination?.total || 0);
        }

        // 2. Fetch the massive sidebar HTML asynchronously in the background so it doesn't block chapter load
        booksAPI.getSidebar(token, bookId)
          .then(sidebarRes => {
            if (sidebarRes.sidebar_html) {
              setSidebarHtml(sidebarRes.sidebar_html);
            }
          })
          .catch(err => console.warn('[SidebarLoad] Failed to load sidebar TOC:', err));

      } catch (e) {
        console.error(e);
        if (e.message?.includes('acess') || e.message?.includes('subscription')) setAccessError(e.message);
      } finally { setLoadingCh(false); }
    })();
  }, [bookId, chPage, getToken]);

  const loadChapter = async (ch, allChs = chapters, total = totalCh) => {
    // CRASH FIX: bump the sequence and capture it locally. Every async
    // continuation below (the initial chunk-0 fetch, and every iteration of
    // the progressive background loop) checks `mySeq === chapterLoadSeqRef.current`
    // before doing any state update or DOM write. If the user clicks another
    // chapter/anchor before this one finishes, that click bumps the ref to a
    // new number, and this call's continuations quietly stop at their next
    // checkpoint instead of racing the new load or piling up as an orphaned
    // background loop.
    const mySeq = ++chapterLoadSeqRef.current;
    // Whatever the PREVIOUS chapter's downloader was doing is now stale —
    // drop the reference immediately so a click on a sidebar anchor can't
    // force-load a chapter that's no longer the active one.
    chunkDownloaderRef.current = null;
    chunkDownloaderStatusRef.current = null;

    setActive(ch); setLoadingContent(true); setToolbarPos({ show: false }); setSelected(''); setAccessError(null);
    setExpandedSubIds(prev => new Set([...prev, ch.content_id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
    try {
      const token = await getToken();
      if (mySeq !== chapterLoadSeqRef.current) return; // superseded while awaiting the token

      // 1. Fetch the first chunk (0) of chapter data instantly from the server
      const res = await booksAPI.getChapter(token, bookId, ch.content_id, { chunk: 0 });
      if (mySeq !== chapterLoadSeqRef.current) return; // superseded while this fetch was in flight
      setChapterData(res.data);

      const idx = allChs.findIndex(c => c.content_id === ch.content_id);
      const pct = total > 0 ? Math.round(((idx + 1) / total) * 100) : 0;
      await saveProgress(String(bookId), pct, ch.content_id);
      if (mySeq !== chapterLoadSeqRef.current) return; // superseded while saving progress

      // Initialize loader indicators
      if (res.data && res.data.has_more_chunks) {
        setProgressChunkLoaded(1);
        // Total pages / 100 pages chunk size
        setTotalChapterChunks(Math.ceil((res.data.total_pages || 100) / 100));
      } else {
        setProgressChunkLoaded(0);
        setTotalChapterChunks(0);
      }

      setLoadingContent(false); // Enable view instantly for the user

      // 2. Fetch subsequent pages progressively in the background if more chunks exist
      if (res.data && res.data.has_more_chunks) {
        // Track the current downloading chunk index in memory
        let nextChunkIndex = res.data.next_chunk;
        let isFetching = false;
        let forceLoadDeferred = null;

        const totalChunks = Math.ceil((res.data.total_pages || 100) / 100);
        chunkDownloaderStatusRef.current = { current: 1, total: totalChunks };

        const fetchNextProgressiveChunk = async (forceLoadAll = false) => {
          if (isFetching) return;
          if (mySeq !== chapterLoadSeqRef.current) return; // this chapter is no longer active — stop the loop
          isFetching = true;
          try {
            const nextRes = await booksAPI.getChapter(token, bookId, ch.content_id, { chunk: nextChunkIndex });
            if (mySeq !== chapterLoadSeqRef.current) return; // superseded while this chunk was in flight — discard
            if (nextRes.data && nextRes.data.chapter_contents) {
              const decryptedNext = await decryptAsync(nextRes.data.chapter_contents);
              if (mySeq !== chapterLoadSeqRef.current) return; // superseded while decrypting — discard, don't append
              if (shadowContentRef.current?.appendChunk) {
                const activeHls = highlights[String(ch.content_id)] || [];
                shadowContentRef.current.appendChunk(
                  decryptedNext,
                  nextChunkIndex,
                  activeHls,
                  shadowContentRef.current.shadowRoot?.__progressiveObserver || null
                );
              }

              const hasMore = nextRes.data.has_more_chunks;
              nextChunkIndex = nextRes.data.next_chunk;
              setProgressChunkLoaded(nextChunkIndex);
              chunkDownloaderStatusRef.current = { current: nextChunkIndex, total: totalChunks };

              if (hasMore) {
                isFetching = false;
                if (forceLoadAll) {
                  // PERF FIX: this used to call fetchNextProgressiveChunk(true) with
                  // no yield at all. Each iteration does a synchronous CryptoJS AES
                  // decrypt() (main-thread, no Web Worker) plus DOM append — with no
                  // gap between iterations, a fast/cached network response chains
                  // these into one unbroken long task and can trip Chrome's "Page
                  // Unresponsive" detector, especially on low-end CPUs. A 0ms
                  // setTimeout still loads "instantly" for the user (no visible
                  // delay) but hands control back to the browser between every
                  // chunk so it can paint the progress bar and process input.
                  setTimeout(() => fetchNextProgressiveChunk(true), 0);
                } else {
                  setTimeout(() => fetchNextProgressiveChunk(false), 150);
                }
              } else {
                if (mySeq === chapterLoadSeqRef.current) {
                  chunkDownloaderRef.current = null;
                  chunkDownloaderStatusRef.current = null;
                  setProgressChunkLoaded(0); // Completed
                  setTotalChapterChunks(0);
                }
                if (forceLoadDeferred) forceLoadDeferred.resolve();
              }
            }
          } catch (chunkErr) {
            console.warn('[ProgressiveLoad] Chunk retrieval failed:', chunkErr);
            isFetching = false;
            if (mySeq === chapterLoadSeqRef.current) {
              chunkDownloaderRef.current = null;
              chunkDownloaderStatusRef.current = null;
              setProgressChunkLoaded(0);
              setTotalChapterChunks(0);
            }
            if (forceLoadDeferred) forceLoadDeferred.reject(chunkErr);
          }
        };

        // Expose a ref hook so handleAnchorClick can force-download all
        // remaining chunks instantly. A ref (scoped to this component
        // instance) rather than a window global — the previous global
        // version meant a stale chapter's downloader could still be invoked
        // by a click after the user had already navigated elsewhere.
        chunkDownloaderRef.current = (forceAll = false) => {
          if (forceAll) {
            if (!forceLoadDeferred) {
              let resFunc, rejFunc;
              const promise = new Promise((resolve, reject) => {
                resFunc = resolve;
                rejFunc = reject;
              });
              forceLoadDeferred = { promise, resolve: resFunc, reject: rejFunc };
              // Trigger instant execution of download loops
              fetchNextProgressiveChunk(true);
            }
            return forceLoadDeferred.promise;
          }
          return Promise.resolve();
        };

        setTimeout(() => fetchNextProgressiveChunk(false), 300);
      } else {
        chunkDownloaderRef.current = null;
        setProgressChunkLoaded(0);
        setTotalChapterChunks(0);
      }
    } catch (e) {
      if (mySeq !== chapterLoadSeqRef.current) return; // superseded — don't show a stale error
      console.error(e);
      if (e.message?.includes('acess') || e.message?.includes('subscription')) setAccessError(e.message);
      setLoadingContent(false);
    }
  };

  const findTargetChapterForAnchor = useCallback((anchorId, linkText) => {
    if (!chapters || chapters.length === 0) return null;

    const isBodyChapter = (c) => {
      const t = (c.chapter_title || c.name || '').toLowerCase();
      return !t.includes('contents') && !t.includes('preface') && !t.includes('abbreviation') &&
        !t.includes('index') && !t.includes('footnote') && !c.isFootnote;
    };

    const readingChapters = chapters.filter(isBodyChapter);

    // If linkText is provided (from sidebar click), test if it matches a main body chapter title!
    if (linkText) {
      const normText = linkText.toLowerCase().replace(/&amp;/g, '&').replace(/\s+/g, ' ');
      // 1. Exact or partial title match in reading chapters
      const matchedCh = readingChapters.find(c => {
        const cTitle = (c.chapter_title || c.name || '').toLowerCase().replace(/&amp;/g, '&').replace(/\s+/g, ' ');
        if (!cTitle || cTitle.includes('contents')) return false; // skip TOC chapters
        return cTitle === normText || normText.includes(cTitle) || cTitle.includes(normText);
      });
      if (matchedCh) return matchedCh;

      // 2. Structural keyword match (e.g. "ORDER XXA — COSTS" matching "Commentary (... Order XXA)")
      const orderMatch = normText.match(/\b(?:order\s+[a-z0-9]+|part\s+[a-z0-9]+)\b/i);
      if (orderMatch) {
        const keyword = orderMatch[0].toLowerCase();
        const kwCh = readingChapters.find(c => {
          const cTitle = (c.chapter_title || c.name || '').toLowerCase();
          return cTitle.includes(keyword);
        });
        if (kwCh) return kwCh;
      }
    }

    if (!anchorId) return activeChapter || readingChapters[0] || chapters[0];

    // Non-page anchors (e.g. Synopsis targets like syn-target-X-Y) belong to the active chapter
    if (anchorId.startsWith('syn-') || (!/^page\d+/i.test(anchorId) && isNaN(Number(anchorId)))) {
      return activeChapter || readingChapters[0] || chapters[0];
    }

    const pageNum = parseInt(anchorId.replace(/\D/g, ''), 10);
    if (isNaN(pageNum)) return activeChapter || readingChapters[0] || chapters[0];

    // Strategy 2: Detailed Contents TOC page fallback.
    // If pageNum <= 25 (front-matter page) and linkText is a sub-topic (e.g. "Introduction 1. General"),
    // front-matter pages 1-25 are TOC index pages. The actual reading text lives in Chapter 3+!
    if (pageNum <= 25 && linkText && !linkText.toLowerCase().includes('contents')) {
      const firstBodyCh = chapters.find(c => {
        const title = (c.chapter_title || c.name || '').toLowerCase();
        return !title.includes('contents') && !title.includes('preface') && (c.content_id >= 3 || c.start_page > 20);
      });
      if (firstBodyCh) return firstBodyCh;
    }

    // Strategy 3: Use pre-parsed pageMap ($O(1)$ fast lookup)
    if (pageMap && pageMap.length > 0) {
      let targetFromMap = null;
      for (const item of pageMap) {
        const t = (item.ch.chapter_title || item.ch.name || '').toLowerCase();
        if (!t.includes('footnote') && !item.ch.isFootnote && item.startPage <= pageNum) {
          targetFromMap = item.ch;
        }
      }
      if (targetFromMap && !targetFromMap.chapter_title?.toLowerCase().includes('contents')) {
        return targetFromMap;
      }
    }

    // If pageNum is within front-matter / Detailed Contents (< 96) and user clicked a sub-topic:
    // Route to the main reading chapter (Commentary)!
    const commentaryCh = chapters.find(c => (c.chapter_title || '').toLowerCase().includes('commentary')) || readingChapters[0];
    if (pageNum < 96 && linkText && commentaryCh) {
      return commentaryCh;
    }

    // Strategy 4: If content_id itself is starting page (e.g. content_id <= pageNum)
    const sorted = [...chapters].sort((a, b) => a.content_id - b.content_id);
    let targetCh = null;
    for (const ch of sorted) {
      if (ch.content_id <= pageNum && ch.content_id < 10000) targetCh = ch;
    }
    if (targetCh) return targetCh;

    // Strategy 5: Fallback to activeChapter or first chapter
    return activeChapter || readingChapters[0] || sorted[0];
  }, [chapters, pageMap, activeChapter]);

  const resolveDetailedAnchor = useCallback((anchorId, linkText) => {
    if (!anchorId) return anchorId;

    // Special case: ORDER XXA in Detailed Contents TOC points to #page62 (TOC table page),
    // but the actual commentary section starts at page 1364 in Commentary chapter.
    if (linkText && /order\s*xxa\b/i.test(linkText)) {
      return 'page1364';
    }

    const pageNum = parseInt(anchorId.replace(/\D/g, ''), 10);
    const bodyThreshold = (pageMap && pageMap.length > 0)
      ? (pageMap.find(item => {
        const t = (item.ch.chapter_title || item.ch.name || '').toLowerCase();
        return !t.includes('contents') && !t.includes('preface') && !t.includes('abbreviation') && !t.includes('index');
      })?.startPage || 96)
      : 96;
    if (isNaN(pageNum) || pageNum >= bodyThreshold) return anchorId;

    const findLinkInContainer = (containerEl) => {
      const links = Array.from(containerEl.querySelectorAll('a[href^="#page"]'));

      const cleanTitle = (str) => {
        if (!str) return '';
        return str
          .toLowerCase()
          .replace(/introduction|section|part|chapter/gi, '')
          .replace(/[\d\.\:\-\_\.]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      };

      // Strategy 1: Match by topic title/text if provided
      if (linkText) {
        const normLinkText = cleanTitle(linkText);
        if (normLinkText) {
          const matchedLink = links.find(a => {
            const aText = cleanTitle(a.textContent || '');
            const h = a.getAttribute('href') || '';
            const num = parseInt(h.replace(/\D/g, ''), 10);
            return !isNaN(num) && num >= bodyThreshold && num < 1368 && aText && (aText.includes(normLinkText) || normLinkText.includes(aText));
          });
          if (matchedLink) {
            const href = matchedLink.getAttribute('href');
            if (href && href.startsWith('#')) return href.substring(1);
          }
        }
      }

      // Strategy 2: First body section link (num > 25)
      const bodyLink = links.find(a => {
        const h = a.getAttribute('href') || '';
        const num = parseInt(h.replace(/\D/g, ''), 10);
        return !isNaN(num) && num > 25;
      });
      if (bodyLink) {
        const href = bodyLink.getAttribute('href');
        if (href && href.startsWith('#')) return href.substring(1);
      }

      return null;
    };

    // 1. Search API-provided sidebarHtml (always ready immediately on page load!)
    if (sidebarHtml && linkText) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sidebarHtml, 'text/html');
        const found = findLinkInContainer(doc.body);
        if (found) return found;
      } catch (e) { }
    }

    // 2. Search rendered Shadow DOM
    if (shadowContentRef.current?.shadowRoot) {
      const targetEl = shadowContentRef.current.shadowRoot.querySelector(`#${CSS.escape(anchorId)}, [id="${anchorId}"]`) || shadowContentRef.current.shadowRoot;
      const found = findLinkInContainer(targetEl);
      if (found) return found;
    }

    // 3. Search unrendered raw HTML in secureMemory
    if (shadowContentRef.current?.getRawHtmlForAnchor) {
      const rawHtml = shadowContentRef.current.getRawHtmlForAnchor(anchorId);
      if (rawHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = rawHtml;
        const targetEl = tmp.querySelector(`#${CSS.escape(anchorId)}, [id="${anchorId}"]`) || tmp;
        const found = findLinkInContainer(targetEl);
        if (found) return found;
      }
    }
    return anchorId;
  }, [sidebarHtml, pageMap]);

  // The actual navigation logic, referenced by handleAnchorClick's debounce
  // below (declared first so that reference is valid — a useCallback's
  // dependency array is evaluated at render time, in source order).
  const navigateToAnchor = useCallback((anchorId, linkText, clearPending) => {
    navRetrySeqRef.current += 1;
    const currentNavSeq = navRetrySeqRef.current;
    const targetIdToUse = resolveDetailedAnchor(anchorId, linkText);

    const navigateWithRetry = () => {
      let attempts = 0;
      const maxAttempts = 20; // 20 x 150ms = up to 3s
      const poll = () => {
        if (currentNavSeq !== navRetrySeqRef.current) return; // Cancelled ONLY if user clicked a newer sidebar link!
        const success = shadowContentRef.current?.scrollToAnchor(targetIdToUse);
        if (success) { clearPending?.(); return; }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 150);
        } else {
          clearPending?.(); // give up quietly rather than leaving the spinner stuck forever
        }
      };
      // Initial delay to allow React state transition to complete if chapter just changed
      setTimeout(poll, 100);
    };

    if (!chapters || chapters.length === 0) { clearPending?.(); return; }
    const targetCh = findTargetChapterForAnchor(targetIdToUse, linkText);
    if (!targetCh) { clearPending?.(); return; }

    if (targetCh.content_id === activeChapter?.content_id) {
      // ── PROGRESSIVE FORCE-LOAD TARGET FOR SIDEBAR CLICK ──
      // If chapter is already active, check if the anchor is already loaded in the Shadow DOM.
      // If it is NOT loaded yet, force-load the rest of the chunks immediately.
      const hasAnchor = shadowContentRef.current?.shadowRoot?.querySelector?.(`#${CSS.escape(targetIdToUse)}, [id="${targetIdToUse}"]`);

      if (!hasAnchor && chunkDownloaderRef.current) {
        setLoadingProgress(1); // Start tracker

        if (activeProgressIntervalRef.current) clearInterval(activeProgressIntervalRef.current);
        activeProgressIntervalRef.current = setInterval(() => {
          if (chunkDownloaderStatusRef.current) {
            const { current, total } = chunkDownloaderStatusRef.current;
            setLoadingProgress(Math.round((current / total) * 100));
          }
        }, 150);

        // Trigger immediate background completion of progressive chunks
        chunkDownloaderRef.current(true).then(() => {
          if (activeProgressIntervalRef.current) {
            clearInterval(activeProgressIntervalRef.current);
            activeProgressIntervalRef.current = null;
          }
          setLoadingProgress(100);
          setTimeout(() => setLoadingProgress(null), 1000); // Fade away
          navigateWithRetry();
        }).catch(() => {
          if (activeProgressIntervalRef.current) {
            clearInterval(activeProgressIntervalRef.current);
            activeProgressIntervalRef.current = null;
          }
          setLoadingProgress(null);
          showToast('Failed to load target pages.');
          clearPending?.();
        });
        return;
      }

      navigateWithRetry();
      return;
    }

    // Load the correct chapter, then navigate to anchor once loaded!
    loadChapter(targetCh).then(() => {
      navigateWithRetry();
    }).catch(() => {
      clearPending?.();
    });
  }, [chapters, activeChapter, findTargetChapterForAnchor, resolveDetailedAnchor]);

  const handleAnchorClick = useCallback((anchorId, linkText) => {
    if (!anchorId) return;

    setPendingAnchorId(anchorId);
    const clearPending = () => setPendingAnchorId(prev => (prev === anchorId ? null : prev));

    if (navDebounceRef.current) clearTimeout(navDebounceRef.current);
    navDebounceRef.current = setTimeout(() => {
      navDebounceRef.current = null;
      navigateToAnchor(anchorId, linkText, clearPending);
    }, 220);
  }, [navigateToAnchor]);

  useEffect(() => {
    window.scrollToPage = (pageId) => {
      if (!pageId) return;
      const cleanId = String(pageId).replace(/^#/, '');
      handleAnchorClick(cleanId);
    };
    return () => {
      delete window.scrollToPage;
    };
  }, [handleAnchorClick]);

  const encryptedChapterContent = chapterData?.chapter_contents || chapterData?.chapter_content || null;
  const encryptedFootnote = chapterData?.footnote || null;
  const encryptedMemoryTickers = chapterData?.memory_tickers || null;
  const activeChapterHighlights = useMemo(() => highlights[String(activeChapter?.content_id)] || [], [highlights, activeChapter?.content_id]);

  // Bookmark scrollTop restore — content render hone ke baad contentRef scroll karo
  useEffect(() => {
    if (!initialScrollTop || loadingContent || !chapterData) return;
    // Multiple attempts — chunks lazy load hote hain
    const attempts = [300, 600, 1000, 1500];
    const timers = attempts.map(delay =>
      setTimeout(() => {
        if (contentRef.current && contentRef.current.scrollTop < initialScrollTop) {
          contentRef.current.scrollTop = initialScrollTop;
        }
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterData, loadingContent]);

  // Highlight scroll — ShadowContent IntersectionObserver chunks load hone ke baad
  useEffect(() => {
    if (!initialHighlight || loadingContent || !chapterData) return;
    const attempts = [800, 1200, 1800];
    const timers = attempts.map(delay =>
      setTimeout(() => {
        if (shadowContentRef.current?.highlightText) {
          shadowContentRef.current.highlightText(initialHighlight, 0);
        }
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterData, loadingContent]);

  const handleLinkClick = useCallback((info) => {
    if (info.type === 'xlink') openXlink(info.value);
    else if (info.type === 'footnote') {
      // scrollToFootnote(info.value);
      setFnTooltip({ show: true, fnText: info.fnText || '', fnNote: info.fnNote || info.value, fnCite: info.fnCite || info.value, clientX: info.clientX || 0, clientY: info.clientY || 0 });
    } else if (info.type === 'anchor') {
      handleAnchorClick(info.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleAnchorClick]);

  const scrollToFootnote = (num) => {
    let root = footnoteRef.current?.shadowRoot;
    if (!root && shadowContentRef.current) {
      root = shadowContentRef.current.shadowRoot;
    }
    if (!root) return;
    const cleanNum = parseInt(String(num).replace(/[^0-9]/g, ''), 10);
    if (!cleanNum || isNaN(cleanNum)) return;
    const content = root.querySelector('.content');
    const listItems = content.querySelectorAll('ol > li');
    if (listItems.length >= cleanNum) {
      const targetEl = listItems[cleanNum - 1];
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetEl.classList.add('footnote-target', 'footnote-highlight');
      setTimeout(() => targetEl.classList.remove('footnote-target', 'footnote-highlight'), 2000);
      return;
    }
    const regex = new RegExp(`^\\W*${cleanNum}[.)\\s]`);
    const walk = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null, false);
    let n;
    while ((n = walk.nextNode())) {
      const text = n.nodeValue.trim();
      if (text && regex.test(text)) {
        let targetEl = n.parentElement;
        if (targetEl === content) { const span = document.createElement('span'); n.parentNode.insertBefore(span, n); span.appendChild(n); targetEl = span; }
        const isInline = ['SPAN', 'B', 'STRONG', 'I', 'EM', 'SUP', 'SUB', 'A'].includes(targetEl.tagName);
        if (isInline && targetEl.parentElement && targetEl.parentElement !== content) targetEl = targetEl.parentElement;
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetEl.classList.add('footnote-target', 'footnote-highlight');
        setTimeout(() => targetEl.classList.remove('footnote-target', 'footnote-highlight'), 2000);
        return;
      }
    }
    footnoteRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleContentClick = (e) => {
    const xl = e.target.dataset?.xlink || e.target.closest?.('[data-xlink]')?.dataset?.xlink;
    if (xl) openXlink(xl);
  };

  const openXlink = async (term) => {
    setXlinkTerm(term); setXlinkResults([]); setShowXlink(true); setXlinkLoading(true);
    try { const res = await searchAPI.search({ q: term, limit: 8 }); setXlinkResults(res.data?.books || []); }
    catch (e) { console.error(e); } finally { setXlinkLoading(false); }
  };

  const handleMouseUp = (e) => {
    // Capture mouse coordinates BEFORE setTimeout — these are always accurate
    // even for Shadow DOM selections where getBoundingClientRect returns zero.
    const mouseX = e?.clientX || 0;
    const mouseY = e?.clientY || 0;
    setTimeout(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim() || '';
      if (text.length > 2) {
        setSelected(text);
        try {
          const TOOLBAR_H = 42;
          const GAP = 10;
          // Try getBoundingClientRect first (works for regular DOM)
          // Falls back to mouse position for Shadow DOM selections
          const r = sel.getRangeAt(0).getBoundingClientRect();
          const hasValidRect = r && r.width > 0 && r.height > 0;
          // X: center of selection rect, or mouse X if rect is zero
          const refX = hasValidRect ? (r.left + r.width / 2) : mouseX;
          // Y: top of selection rect, or mouse Y if rect is zero
          const refY = hasValidRect ? r.top : mouseY;
          // Place toolbar above the reference point; show below if too close to screen top
          const top = refY - TOOLBAR_H - GAP > 8
            ? refY - TOOLBAR_H - GAP
            : refY + GAP + 24;
          setToolbarPos({ show: true, top, left: refX });
        } catch {
          // Full fallback: position at mouse cursor
          setToolbarPos({ show: true, top: Math.max(8, mouseY - 52), left: mouseX });
        }
      } else if (!showAnnot) { setToolbarPos({ show: false }); setSelected(''); }
    }, 10);
  };

  const handleHighlight = async (colorName) => {
    if (!selectedText || !activeChapter) return;
    // Scroll position save karo — blink prevent karne ke liye
    const scrollTop = contentRef.current?.scrollTop || 0;
    setToolbarPos({ show: false });
    window.getSelection()?.removeAllRanges();
    setSelected('');
    await addAnnotation({ type: 'highlight', color: colorName, bookId, bookName: decrypt(book?.book_name) || '', chapterId: activeChapter.content_id, chapterTitle: decrypt(activeChapter.chapter_title) || '', selectedText, note: null, tags: [] });
    // Scroll restore karo — no blink
    requestAnimationFrame(() => {
      if (contentRef.current) contentRef.current.scrollTop = scrollTop;
    });
    showToast(`✓ Highlighted in ${colorName} — saved`);
  };

  const handleCopyCite = () => {
    navigator.clipboard?.writeText(`${selectedText} [${decrypt(book?.book_name) || ''}, ${decrypt(activeChapter?.chapter_title) || ''}, AIR]`).catch(() => { });
    showToast('✓ Copied with AIR citation');
    setToolbarPos({ show: false });
  };

  const handleSaveNote = async () => {
    if (!selectedText && !noteText) return;
    await addAnnotation({ type: 'note', color: 'blue', bookId, bookName: decrypt(book?.book_name) || '', chapterId: activeChapter?.content_id || null, chapterTitle: decrypt(activeChapter?.chapter_title) || '', selectedText: selectedText || '(Note)', note: noteText, tags: [] });
    showToast('✓ Note saved');
    setNoteText(''); setSelected(''); setShowAnnot(false); setToolbarPos({ show: false });
    window.getSelection()?.removeAllRanges();
  };

  const handleBookmark = async () => {
    const scrollTop = contentRef.current?.scrollTop || 0;
    await toggleBookmark(String(bookId), activeChapter?.content_id, scrollTop);
    showToast(isBookmarked ? 'Bookmark removed' : '✓ Bookmarked');
    setToolbarPos({ show: false });
  };

  const handleShare = () => {
    navigator.clipboard?.writeText(`${window.location.origin}?book=${bookId}&chapter=${activeChapter?.content_id}`).catch(() => { });
    showToast('✓ Deep link copied');
    setToolbarPos({ show: false });
  };

  const handleInSearch = (term) => {
    setInSearch(term);
    if (!term.trim() || (!chapterData?.chapter_contents && !chapterData?.chapter_content)) { setSearchHits([]); return; }
    const fullText = decrypt(chapterData.chapter_contents || chapterData.chapter_content).replace(/<[^>]*>/g, '');
    const hits = [];
    let i = fullText.toLowerCase().indexOf(term.toLowerCase());
    let occurrence = 0;
    while (i !== -1 && hits.length < 25) {
      hits.push({ snippet: fullText.substring(Math.max(0, i - 30), i + 70), index: i, term, occurrence });
      i = fullText.toLowerCase().indexOf(term.toLowerCase(), i + 1);
      occurrence++;
    }
    setSearchHits(hits);
  };

  const scrollToSearchHit = (hit) => { if (shadowContentRef.current?.highlightText) shadowContentRef.current.highlightText(hit.term, hit.occurrence); };

  useEffect(() => {
    const fn = e => {
      if (e.key === 'Escape') {
        setToolbarPos({ show: false }); setSelected(''); setShowXlink(false); setShowSearch(false); setShowSettings(false); setShowAnnot(false);
      }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 's' || e.key.toLowerCase() === 'p')) {
        e.preventDefault();
        showToast('Saving or printing is disabled for security reasons.');
      }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);

  useEffect(() => {
    const handleClickAway = (e) => { if (toolbarPos.show && !e.target.closest('.selection-toolbar')) setToolbarPos(prev => ({ ...prev, show: false })); };
    window.addEventListener('mousedown', handleClickAway);
    return () => window.removeEventListener('mousedown', handleClickAway);
  }, [toolbarPos.show]);

  const outerStyle = { display: 'flex', height: '100vh', background: t.bg, color: t.text, fontFamily: 'Nunito,system-ui,sans-serif', overflow: 'hidden' };
  const tocStyle = { width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${t.border}`, background: t.toolbar };
  const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 };
  const barStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: `1px solid ${t.border}`, background: t.toolbar, flexShrink: 0 };
  const btnStyle = { background: 'none', border: 'none', cursor: 'pointer' };

  return (
    <div style={outerStyle}>

      {/* TOC */}
      {showToc && (
        <div style={tocStyle}>
          <div style={{ ...barStyle, gap: 8 }}>
            <button onClick={onBack} style={{ ...btnStyle, color: 'var(--rose)', fontSize: 16, fontWeight: 700 }}>←</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{decrypt(book?.book_name) || 'Book'}</div>
              <div style={{ fontSize: 9, color: t.subText }}>{totalCh} chapters</div>
            </div>
            <button onClick={() => setShowToc(false)} style={{ ...btnStyle, color: t.subText, fontSize: 12 }}>✕</button>
          </div>
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: t.subText, marginBottom: 4 }}><span>Progress</span><span>{progress}%</span></div>
            <div style={{ height: 2, borderRadius: 2, background: t.border }}><div style={{ height: '100%', borderRadius: 2, width: `${progress}%`, background: 'var(--rose-lt)', transition: 'width .4s' }} /></div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingCh ? <div style={{ padding: 16, textAlign: 'center', fontSize: 11, color: t.subText }}>Loading…</div>
              : (() => {
                return chapters.map((ch, i) => {
                  const titleRaw = decrypt(ch.chapter_title || ch.name) || '';
                  const titleNorm = titleRaw.toLowerCase().trim();
                  const isActive = activeChapter?.content_id === ch.content_id || pendingChapterId === ch.content_id;

                  // Find the best matching key in subMap for this chapter
                  // (exact match first, then partial)
                  let matchedSubHtml = subMap[titleNorm];
                  if (!matchedSubHtml) {
                    for (const [key, val] of Object.entries(subMap)) {
                      if (titleNorm.includes(key) || key.includes(titleNorm)) {
                        matchedSubHtml = val;
                        break;
                      }
                    }
                  }

                  const hasSubTree = !!matchedSubHtml;
                  const isExpanded = expandedSubIds.has(ch.content_id);

                  return (
                    <React.Fragment key={ch.content_id}>
                      <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${t.border}`, background: isActive ? 'rgba(201,123,119,.10)' : 'transparent' }}>
                        <button
                          onClick={() => {
                            // Instant feedback — see pendingChapterId. The
                            // isActive highlight below can't do this by
                            // itself anymore since loadChapter (and the
                            // setActive() inside it) is now debounced.
                            setPendingChapterId(ch.content_id);
                            // Same debounce as handleAnchorClick — see
                            // navDebounceRef comment above. Shares the same
                            // ref, so a rapid mix of top-level chapter
                            // clicks and sidebar leaf-link clicks all
                            // collapse into whichever one the user actually
                            // stopped on.
                            if (navDebounceRef.current) clearTimeout(navDebounceRef.current);
                            navDebounceRef.current = setTimeout(() => {
                              navDebounceRef.current = null;
                              setPendingChapterId(prev => (prev === ch.content_id ? null : prev));
                              loadChapter(ch);
                            }, 220);
                          }}
                          style={{ flex: 1, textAlign: 'left', display: 'flex', gap: 8, padding: '10px 12px', cursor: 'pointer', border: 'none', background: 'transparent' }}>
                          <span style={{ fontSize: 9, color: 'var(--rose)', minWidth: 20, fontWeight: 700 }}>{i + 1}</span>
                          <span style={{ fontSize: 11, lineHeight: 1.4, color: isActive ? 'var(--rose)' : t.text }}>{titleRaw || 'Chapter'}</span>
                        </button>
                        {hasSubTree && (
                          <button
                            title={isExpanded ? 'Collapse' : 'Expand sub-topics'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedSubIds(prev => {
                                const next = new Set(prev);
                                if (next.has(ch.content_id)) next.delete(ch.content_id);
                                else next.add(ch.content_id);
                                return next;
                              });
                            }}
                            style={{ flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', padding: '0 12px', fontSize: 14, fontWeight: 700, color: isExpanded ? 'var(--rose)' : 'rgba(201,123,119,0.55)', display: 'flex', alignItems: 'center' }}>
                            {isExpanded ? '−' : '+'}
                          </button>
                        )}
                      </div>
                      {hasSubTree && isExpanded && (
                        <PDFStudioSidebarTree
                          subHtml={matchedSubHtml}
                          onSelectAnchor={handleAnchorClick}
                          textColor={t.text}
                          pendingAnchorId={pendingAnchorId}
                        />
                      )}
                    </React.Fragment>
                  );
                });
              })()}
            {chPag && chPag.total_pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button disabled={!chPag.has_prev} onClick={() => setChPage(p => p - 1)} style={{ ...btnStyle, padding: '4px 8px', border: `1px solid ${t.border}`, borderRadius: 4, color: t.subText, fontSize: 11 }}>‹</button>
                <span style={{ fontSize: 11, color: t.subText, padding: '4px 6px' }}>{chPag.page}/{chPag.total_pages}</span>
                <button disabled={!chPag.has_next} onClick={() => setChPage(p => p + 1)} style={{ ...btnStyle, padding: '4px 8px', border: `1px solid ${t.border}`, borderRadius: 4, color: t.subText, fontSize: 11 }}>›</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main column */}
      <div style={mainStyle}>
        <div style={barStyle}>
          {!showToc && <button onClick={() => setShowToc(true)} style={{ ...btnStyle, fontSize: 16, color: t.subText }}>☰</button>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{decrypt(book?.book_name)}</div>
            {activeChapter && <div style={{ fontSize: 9, color: t.subText }}>{decrypt(activeChapter.chapter_title || activeChapter.name)}</div>}
          </div>
          <button onClick={() => { setShowSearch(!showSearch); setShowSettings(false); setShowAnnot(false); }} style={{ ...btnStyle, fontSize: 15, color: showSearch ? 'var(--rose)' : t.subText }}>🔍</button>
          <button onClick={handleShare} style={{ ...btnStyle, fontSize: 13, color: t.subText }}>🔗</button>
        </div>

        <div style={{ height: 2, flexShrink: 0, background: t.border }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'var(--rose-lt)', transition: 'width .4s' }} />
        </div>

        {showSearch && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: `1px solid ${t.border}`, background: t.toolbar }}>
            <input value={inSearch} onChange={e => handleInSearch(e.target.value)} autoFocus placeholder="Search in this chapter…"
              style={{ flex: 1, padding: '8px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: t.bg, color: t.text, fontFamily: 'Nunito,sans-serif', fontSize: 13, outline: 'none' }} />
            {searchHits.length > 0 && <span style={{ fontSize: 11, color: t.subText, whiteSpace: 'nowrap' }}>{searchHits.length} matches</span>}
            <button onClick={() => { setShowSearch(false); setInSearch(''); setSearchHits([]); }} style={{ ...btnStyle, color: t.subText, fontSize: 16, fontWeight: 700 }}>✕</button>
          </div>
        )}

        {showSearch && searchHits.length > 0 && (
          <div style={{ flexShrink: 0, maxHeight: 130, overflowY: 'auto', borderBottom: `1px solid ${t.border}`, background: t.toolbar }}>
            {searchHits.map((h, i) => (
              <div key={i} onClick={() => scrollToSearchHit(h)}
                style={{ padding: '8px 16px', borderBottom: `1px solid ${t.border}`, fontSize: 11, color: t.subText, fontFamily: 'Lora,serif', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,123,119,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                …{h.snippet}…
              </div>
            ))}
          </div>
        )}

        {/* Progressive Background Loading Bar */}
        {totalChapterChunks > 0 && progressChunkLoaded > 0 && (
          <div style={{ height: 4, width: '100%', background: t.border, flexShrink: 0, position: 'relative' }}>
            <div style={{
              height: '100%',
              width: `${(progressChunkLoaded / totalChapterChunks) * 100}%`,
              background: 'var(--rose)',
              transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }} />
            <div style={{
              position: 'absolute',
              top: 8,
              right: 16,
              fontSize: '10px',
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 800,
              color: 'var(--rose)',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: t.toolbar,
              border: `1.5px solid ${t.border}`,
              padding: '4px 10px',
              borderRadius: '6px',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
              zIndex: 10,
              pointerEvents: 'none'
            }}>
              Loading Pages ({Math.round((progressChunkLoaded / totalChapterChunks) * 100)}%)
            </div>
          </div>
        )}

        <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', userSelect: 'text', WebkitUserSelect: 'text' }}
          onMouseUp={handleMouseUp} onClick={handleContentClick}>
          <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 24px 40px' }}>
            {loadingContent ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <div style={{ width: 28, height: 28, border: `2px solid ${t.border}`, borderTopColor: 'var(--rose)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
              </div>
            ) : accessError ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, textAlign: 'center', padding: 40, background: 'var(--rose-mist)', borderRadius: 20, border: '1px dashed var(--rose-lt)' }}>
                <div style={{ fontSize: 48, marginBottom: 20 }}>🔒</div>
                <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 22, fontWeight: 700, color: 'var(--rose)', marginBottom: 12 }}>Subscription Required</div>
                <p style={{ fontFamily: 'Nunito,sans-serif', fontSize: 14, color: 'var(--ink-mid)', lineHeight: 1.6, maxWidth: 400 }}>{accessError}</p>
                <button onClick={onBack} style={{ marginTop: 24, padding: '10px 24px', background: 'var(--rose)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Browse Other Books</button>
              </div>
            ) : chapterData ? (
              <>
                <div style={{ fontFamily: 'Playfair Display,serif', fontSize: fontSize + 6, fontWeight: 700, color: 'var(--rose)', lineHeight: 1.3, marginBottom: 4 }}>{decrypt(chapterData.chapter_title || chapterData.name)}</div>
                {chapterData.chapter_number && <div style={{ fontSize: 10, fontWeight: 700, color: t.subText, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Nunito,sans-serif', marginBottom: 4 }}>Chapter {chapterData.chapter_number}</div>}
                <div style={{ fontSize: 10, color: t.subText, borderBottom: `1px solid ${t.border}`, paddingBottom: 12, marginBottom: 16, fontFamily: 'Nunito,sans-serif' }}>{decrypt(book?.book_name)}</div>
                <div onClick={() => openXlink(decrypt(chapterData.chapter_title) || decrypt(book?.book_name) || '')}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, padding: '8px 11px', background: 'var(--rose-pale)', border: '1px solid var(--rose-lt)', borderLeft: '3px solid var(--rose)', borderRadius: '0 7px 7px 0', fontSize: 11, color: 'var(--ink-mid)', cursor: 'pointer' }}>
                  <span>📜 View related sections in AIR Bare Acts →</span>
                  <span style={{ color: 'var(--rose)', fontWeight: 700, fontSize: 14 }}>›</span>
                </div>

                <MemoShadowContent
                  html={encryptedChapterContent}
                  footnoteHtml={encryptedFootnote}
                  highlights={activeChapterHighlights}
                  style={{ fontSize, color: t.text }}
                  isWiped={isWiped}
                  wipeReason={wipeReason}
                  watermarkText={watermarkText}
                  onLinkClick={handleLinkClick}
                  ref={shadowContentRef}
                  chapterTitle={decrypt(chapterData?.chapter_title || chapterData?.name)}
                  chunkDownloaderRef={chunkDownloaderRef}
                />

                {encryptedFootnote && (
                  <div style={{ marginTop: 32, paddingTop: 16, borderTop: `1px solid ${t.border}` }}>
                    <div style={{ fontSize: 9, color: t.subText, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Nunito,sans-serif', fontWeight: 700, marginBottom: 8 }}>Footnotes</div>
                    <MemoShadowContent html={encryptedFootnote} highlights={EMPTY_HIGHLIGHTS} style={{ fontSize: 11, color: t.subText, fontFamily: 'Lora,serif' }} isWiped={isWiped} wipeReason={wipeReason} watermarkText={watermarkText} onLinkClick={handleLinkClick} ref={footnoteRef} disableLazy={true} chapterTitle={decrypt(chapterData?.chapter_title || chapterData?.name)} />
                  </div>
                )}

                {encryptedMemoryTickers && (
                  <div style={{ marginTop: 24, padding: 16, borderRadius: 12, border: '1px solid #C8E8D8', background: 'var(--sage-pale)' }}>
                    <div style={{ fontSize: 9, color: 'var(--sage)', letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Nunito,sans-serif', fontWeight: 700, marginBottom: 8 }}>Memory Tickers</div>
                    <MemoShadowContent html={encryptedMemoryTickers} highlights={EMPTY_HIGHLIGHTS} style={{ fontSize: 12, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }} isWiped={isWiped} wipeReason={wipeReason} watermarkText={watermarkText} onLinkClick={handleLinkClick} />
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, opacity: .3 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📖</div>
                <p style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: t.subText }}>Select a chapter</p>
              </div>
            )}
          </div>
        </div>

        {showSettings && (
          <div style={{ flexShrink: 0, borderTop: `2px solid var(--rose-lt)`, padding: '20px 24px', background: t.toolbar, boxShadow: '0 -4px 16px rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.subText, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Nunito,sans-serif', marginBottom: 10 }}>Reading Theme</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {Object.entries(THEMES).map(([key, th]) => (
                  <button key={key} onClick={() => { setReadingTheme(key); setShowSettings(false); }}
                    style={{ padding: '10px 20px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'Nunito,sans-serif', background: readingTheme === key ? 'var(--rose)' : t.bg, color: readingTheme === key ? 'white' : t.text, border: `2px solid ${readingTheme === key ? 'var(--rose)' : t.border}` }}>
                    {th.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.subText, letterSpacing: 2, textTransform: 'uppercase', fontFamily: 'Nunito,sans-serif', marginBottom: 10 }}>Font Size</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={() => setFontSize(s => Math.max(12, s - 1))} style={{ padding: '8px 16px', border: `2px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 16, cursor: 'pointer', background: t.bg, fontWeight: 700 }}>A−</button>
                <span style={{ fontSize: 15, color: t.text, minWidth: 50, textAlign: 'center', fontFamily: 'Nunito,sans-serif', fontWeight: 700 }}>{fontSize}px</span>
                <button onClick={() => setFontSize(s => Math.min(22, s + 1))} style={{ padding: '8px 16px', border: `2px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 18, cursor: 'pointer', background: t.bg, fontWeight: 700 }}>A+</button>
              </div>
            </div>
            <button onClick={() => setShowSettings(false)} style={{ marginLeft: 'auto', padding: '8px 16px', border: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer', color: t.subText, background: 'transparent', fontSize: 13, fontFamily: 'Nunito,sans-serif' }}>Close ✕</button>
          </div>
        )}

        <div style={{ flexShrink: 0, display: 'flex', borderTop: `1px solid ${t.border}`, background: t.toolbar, minHeight: 38 }}>
          {[
            { ico: '☰', lbl: 'TOC', fn: () => setShowToc(!showToc) },
            { ico: '🔍', lbl: 'Search', fn: () => { setShowSearch(!showSearch); setShowSettings(false); setShowAnnot(false); }, active: showSearch },
            { ico: '🖊', lbl: 'Annotate', fn: () => { setShowAnnot(!showAnnot); setShowSettings(false); setShowSearch(false); }, active: showAnnot },
            { ico: '🔖', lbl: 'Bookmark', fn: handleBookmark, active: isBookmarked },
            { ico: '☀', lbl: 'Theme', fn: () => { setShowSettings(!showSettings); setShowAnnot(false); setShowSearch(false); }, active: showSettings },
          ].map(tb => (
            <button key={tb.lbl} onClick={tb.fn}
              style={{ flex: 1, textAlign: 'center', cursor: 'pointer', background: tb.active ? 'rgba(201,123,119,.08)' : 'none', border: 'none', padding: '4px 0' }}>
              <div style={{ fontSize: 15, display: 'block', marginBottom: 1, color: tb.active ? 'var(--rose)' : t.subText }}>{tb.ico}</div>
              <div style={{ fontSize: 8, color: tb.active ? 'var(--rose)' : t.subText, fontFamily: 'Nunito,sans-serif', fontWeight: 700 }}>{tb.lbl}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Annotation Sidebar */}
      {showAnnot && (
        <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${t.border}`, background: t.toolbar, animation: 'growLeft 0.3s ease-out', overflow: 'hidden' }}>
          <div style={{ ...barStyle, justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--rose)', textTransform: 'uppercase', letterSpacing: 1 }}>Note Taker</span>
            <button onClick={() => setShowAnnot(false)} style={{ ...btnStyle, color: t.subText, fontSize: 14 }}>✕</button>
          </div>
          <div style={{ padding: 20, flex: 1, overflowY: 'auto', minWidth: 320 }}>
            {selectedText ? (
              <div style={{ marginBottom: 20, padding: 12, borderRadius: 8, borderLeft: '3px solid var(--rose)', background: t.bg, color: t.subText, fontFamily: 'Lora,serif', fontSize: 12, fontStyle: 'italic', lineHeight: 1.6 }}>
                "{selectedText.length > 200 ? selectedText.substring(0, 200) + '...' : selectedText}"
              </div>
            ) : (
              <div style={{ marginBottom: 20, padding: 12, borderRadius: 8, border: '1px dashed var(--border)', color: t.subText, fontSize: 11, textAlign: 'center' }}>
                Select text in the book to add a specific reference to your note.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: t.subText, marginBottom: 8, textTransform: 'uppercase' }}>Your Notes</label>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Type your thoughts here..."
                  rows={8}
                  autoFocus
                  style={{ width: '100%', padding: 12, borderRadius: 10, border: `1px solid ${t.border}`, background: t.bg, color: t.text, fontFamily: 'Nunito,sans-serif', fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={handleSaveNote}
                  style={{ width: '100%', padding: '12px', background: 'var(--rose)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'Nunito,sans-serif' }}>
                  Save Annotation
                </button>
                <button onClick={() => { setShowAnnot(false); setNoteText(''); }}
                  style={{ width: '100%', padding: '12px', border: `1px solid ${t.border}`, borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13, color: t.subText, background: 'transparent', fontFamily: 'Nunito,sans-serif' }}>
                  Discard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes growLeft { from { width: 0; opacity: 0; } to { width: 320px; opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {toolbarPos.show && (
        <div className="selection-toolbar" style={{ position: 'fixed', top: toolbarPos.top, left: toolbarPos.left, transform: 'translateX(-50%)', zIndex: 9999, display: 'flex', background: '#2C2C35', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.5)', overflow: 'hidden', maxWidth: '95vw', flexWrap: 'wrap' }}>
          {HL_COLORS.map(c => (
            <button key={c.name} onMouseDown={e => { e.preventDefault(); handleHighlight(c.name); }}
              style={{ padding: '8px 10px', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,.9)', fontFamily: 'Nunito,sans-serif', background: 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: c.bg, display: 'inline-block', border: '1px solid rgba(255,255,255,.3)', flexShrink: 0 }} />{c.label}
            </button>
          ))}
          {[
            { lbl: '📝 Note', fn: () => { setShowAnnot(true); setToolbarPos({ show: false }); } },
            { lbl: '📋 Copy+Cite', fn: handleCopyCite, color: '#E8CFA0' },
            { lbl: '🔖 Bookmark', fn: handleBookmark },
            { lbl: '🔗 Share', fn: handleShare },
          ].map(b => (
            <button key={b.lbl} onMouseDown={e => { e.preventDefault(); b.fn(); }}
              style={{ padding: '8px 10px', fontSize: 9, fontWeight: 700, color: b.color || 'rgba(255,255,255,.9)', fontFamily: 'Nunito,sans-serif', background: 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.12)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {b.lbl}
            </button>
          ))}
        </div>
      )}

      {showXlink && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(44,44,53,.55)' }} onClick={() => setShowXlink(false)}>
          <div style={{ background: 'white', borderRadius: 20, padding: 24, maxWidth: 460, width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'Playfair Display,serif', fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>Related Bare Act Sections</div>
            <div style={{ marginBottom: 16, padding: '8px 12px', background: 'var(--rose-pale)', borderRadius: 8, fontSize: 13, color: 'var(--rose)', fontFamily: 'Nunito,sans-serif', fontWeight: 600 }}>{xlinkTerm}</div>
            {xlinkLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><div style={{ width: 24, height: 24, border: '2px solid var(--border)', borderTopColor: 'var(--rose)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} /></div>
            ) : xlinkResults.length > 0 ? (
              <div>
                <div style={{ fontSize: 10, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Found in {xlinkResults.length} books — click to open</div>
                {xlinkResults.map(r => (
                  <div key={r._id} onClick={() => { setShowXlink(false); if (onOpenBook) onOpenBook(r); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, border: '1px solid var(--border)', marginBottom: 8, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--rose-pale)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>{decrypt(r.book_name || r.book_title)}</div>
                      {r.category?.name && <div style={{ fontSize: 10, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', marginTop: 2 }}>{r.category.name}</div>}
                    </div>
                    <span style={{ color: 'var(--rose)', fontWeight: 700, fontSize: 18 }}>›</span>
                  </div>
                ))}
              </div>
            ) : <p style={{ fontSize: 13, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif', lineHeight: 1.6 }}>No matches found for "{xlinkTerm}"</p>}
            <button onClick={() => setShowXlink(false)} style={{ marginTop: 16, width: '100%', padding: 10, borderRadius: 12, border: '1px solid var(--border)', color: 'var(--ink-mid)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito,sans-serif', background: 'transparent' }}>Close</button>
          </div>
        </div>
      )}

      {fnTooltip?.show && (() => {
        const M = 14;
        const W = Math.min(380, window.innerWidth - 2 * M);
        const H = 300;
        const bottomNavOffset = 60;
        const maxAllowedY = window.innerHeight - bottomNavOffset - M;

        let x = (fnTooltip.clientX || 0) + M;
        let y = (fnTooltip.clientY || 0) + M;

        if (x + W > window.innerWidth - M) x = (fnTooltip.clientX || 0) - W - M;
        if (x < M) x = M;

        // If tooltip would extend off the bottom of the screen, show it above the click point
        if (y + H > maxAllowedY) {
          y = (fnTooltip.clientY || 0) - H - M;
        }
        if (y + H > maxAllowedY) {
          y = maxAllowedY - H;
        }
        if (y < M) y = M;

        const maxContentH = Math.max(120, Math.min(280, window.innerHeight - y - 110));

        return (
          <>
            <div onClick={() => setFnTooltip(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
            <div style={{
              position: 'fixed', left: x, top: y, width: W, zIndex: 9999,
              background: 'rgba(30, 26, 21, 0.96)', backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(201, 168, 76, 0.25)',
              borderLeft: '4px solid #c9a84c', borderRadius: '4px 12px 12px 4px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.65)', fontFamily: "'Nunito',sans-serif",
              overflow: 'hidden', animation: 'fnTtIn 0.18s cubic-bezier(0.23, 1, 0.32, 1)',
            }}>
              <div style={{ background: 'rgba(42, 36, 32, 0.85)', padding: '8px 14px 7px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid rgba(201, 168, 76, 0.15)' }}>
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: '#c9a84c', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 3, padding: '2px 6px' }}>Footnote</span>
                {(fnTooltip.fnNote || fnTooltip.fnCite) && (
                  <span style={{ fontSize: 10, color: '#a6927d', fontWeight: 600 }}>
                    {fnTooltip.fnNote ? (fnTooltip.fnNote.includes('Note') ? fnTooltip.fnNote : `Note ${fnTooltip.fnNote}`) : ''}{fnTooltip.fnNote && fnTooltip.fnCite ? '  ·  ' : ''}{fnTooltip.fnCite ? `Ref ${fnTooltip.fnCite}` : ''}
                  </span>
                )}
                <span onClick={() => setFnTooltip(null)} style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: 18, color: '#a6927d', padding: '0 4px', lineHeight: 1, transition: 'color 0.2s' }} onMouseEnter={(e) => e.target.style.color = '#c9a84c'} onMouseLeave={(e) => e.target.style.color = '#a6927d'}>×</span>
              </div>
              <div style={{ padding: '12px 16px 14px', color: '#eae1d8', fontSize: 13, lineHeight: 1.6, maxHeight: `${maxContentH}px`, overflowY: 'auto' }}>
                {fnTooltip.fnText ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {fnTooltip.fnText.split('\n').map((line, idx) => {
                      // Extract prefix if it is numbered (like "1. ", "2. ")
                      const match = line.match(/^(\d+)[\.\s-]+([\s\S]*)/);
                      const displayNum = match ? match[1] : (idx + 1);
                      const displayText = match ? match[2] : line;

                      return (
                        <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', paddingBottom: idx < fnTooltip.fnText.split('\n').length - 1 ? '10px' : '0', borderBottom: idx < fnTooltip.fnText.split('\n').length - 1 ? '1px solid rgba(201,168,76,0.08)' : 'none' }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '18px', height: '18px', borderRadius: '50%', background: 'rgba(201, 168, 76, 0.12)', color: '#c9a84c', fontSize: '9.5px', fontWeight: 700, marginTop: '2px' }}>
                            {displayNum}
                          </span>
                          <span style={{ flex: 1, color: '#eae1d8' }}>{displayText}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span style={{ color: '#8a7a68', fontStyle: 'italic' }}>See footnote {fnTooltip.fnCite} in the section below.</span>
                )}
              </div>
              <div style={{ padding: '6px 14px', background: 'rgba(24, 20, 16, 0.95)', fontSize: 9.5, color: '#8a7a68', borderTop: '1px solid rgba(201,168,76,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Click <kbd style={{ background: '#2a2218', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 2, padding: '0 4px', fontSize: 9, color: '#c9a84c' }}>sup</kbd> again to scroll to reference</span>
              </div>
            </div>
            <style>{`@keyframes fnTtIn{from{opacity:0;transform:translateY(8px) scale(0.96)}to{opacity:1;transform:none}}`}</style>
          </>
        );
      })()}

      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: 'var(--sage)', color: 'white', padding: '10px 20px', borderRadius: 12, fontSize: 12, fontWeight: 700, fontFamily: 'Nunito,sans-serif', whiteSpace: 'nowrap', boxShadow: '0 4px 20px rgba(0,0,0,.2)' }}>
          {toast}
        </div>
      )}

      {/* Target Page Force-Load Status Overlay - Matching the Theme */}
      {loadingProgress !== null && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          background: t.toolbar,
          border: `1.5px solid ${t.border}`,
          color: t.text,
          padding: '14px 24px',
          borderRadius: '16px',
          fontSize: '13px',
          fontFamily: 'Nunito, sans-serif',
          fontWeight: 700,
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          minWidth: '220px',
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '12px',
              height: '12px',
              border: `2px solid ${t.border}`,
              borderTopColor: 'var(--rose)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>{loadingProgress === 100 ? 'Rendering Pages...' : 'Loading Target Section...'}</span>
          </div>
          <div style={{ width: '100%', height: '4px', background: t.border, borderRadius: '2px', overflow: 'hidden', marginTop: '4px' }}>
            <div style={{ height: '100%', width: `${loadingProgress}%`, background: 'var(--rose)', transition: 'width 0.2s ease' }} />
          </div>
          <span style={{ fontSize: '10px', color: t.subText }}>{loadingProgress}% Loaded</span>
        </div>
      )}
    </div>
  );
};

export default OldAndPdfStudioReaderPage;